import { ScriptBlocker } from './ScriptBlocker.js';
import { SettingsModal } from './SettingsModal.js';
import { ContentBlocker } from './ContentBlocker.js';
import { TrackingDataPreserver } from './TrackingDataPreserver.js';
import { TurnstileFormGuard } from './TurnstileFormGuard.js';
import { RecaptchaFormGuard } from './RecaptchaFormGuard.js';
import { pmLog, pmWarn, pmError } from './Logger.js';

/**
 * Liefert true, wenn der Besucher unter Systemeinstellungen "Bewegungen
 * reduzieren" aktiviert hat. Die reine CSS-Media-Query (siehe pixelmate.css,
 * die im echten Frontend allerdings gar nicht geladen wird - siehe
 * injectA11yStyles()) deckt nur CSS-Transitions/-Animationen ab, nicht die
 * mit setTimeout/requestAnimationFrame verketteten JS-Animationen von
 * SmoothExpander und SettingsModal.open()/close() - deshalb hier separat
 * geprüft (WCAG 2.3.3).
 *
 * Zusätzlich respektiert (mit Vorrang), sofern gesetzt: die Admin-Einstellung
 * "Animationen für alle Besucher deaktivieren" (pixelmate_force_reduced_motion,
 * Barrierefreiheits-Karte in den Haupteinstellungen) - erzwingt reduzierte
 * Bewegung unabhängig von der individuellen Geräteeinstellung des Besuchers.
 */
function prefersReducedMotion() {
    if (typeof window !== 'undefined' && window.pixelmateConfig?.settings?.forceReducedMotion) {
        return true;
    }

    return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * SmoothExpander — koordinierte Zwei-Phasen-Animation für Banner-Expansion.
 * Phase 1: Container sanft auf gemessene Zielhöhe expandieren
 * Phase 2: Content-Elemente überlappend einblenden (ab 40% der Container-Animation)
 */
class SmoothExpander {
    constructor() {
        this.isAnimating = false;
        this._cleanupFns = [];
        this._contentTimers = [];
    }

    /**
     * Misst die tatsächliche Höhe des Containers wenn voll expandiert.
     *
     * WICHTIG: scrollHeight gibt bereits die vollständige Content-Höhe zurück,
     * unabhängig von max-height. container.style.maxHeight wird NICHT verändert —
     * das Ändern auf 'none' würde via forciertem Reflow den Browser veranlassen,
     * 'none' als committed state zu speichern. Wenn dann der RAF max-height auf
     * einen px-Wert setzt, versucht CSS von 'none' zu interpolieren, was nicht
     * animierbar ist (kein numerischer Wert), und die Transition schlägt fehl.
     */
    measureExpandedHeight(container, contentElements) {
        // Content-Elemente temporär in Layout-sichtbaren Zustand versetzen (visibility:hidden
        // entfernt sie nicht aus dem Layout — ihre Höhe fließt weiterhin in scrollHeight ein)
        const savedStates = contentElements.map(item => {
            const el = item.el;
            const saved = {
                visibility: el.style.visibility,
                opacity: el.style.opacity,
                maxHeight: el.style.maxHeight,
                overflow: el.style.overflow,
                position: el.style.position
            };
            el.style.visibility = 'hidden';
            el.style.opacity = '0';
            el.style.maxHeight = 'none';
            el.style.overflow = 'visible';
            el.style.position = 'relative';
            return saved;
        });

        // Der Scroll-Bereich (siehe getBannerHTML()) begrenzt sich normalerweise selbst
        // per flex:1/min-height:0/overflow-y:auto, damit Buttons/Footer darunter fix
        // sichtbar bleiben. An dieser Stelle steht container.maxHeight aber noch auf der
        // kollabierten Höhe (Schritt 0 in expand()) - ohne diese Ausnahme würde der
        // Scroll-Bereich innerhalb dieser kleinen Höhe intern clippen/scrollen, statt
        // seine wahre Ziel-Höhe an container.scrollHeight durchzureichen.
        const scrollArea = container.querySelector('.pixelmate-banner-scroll-area');
        const scrollAreaSaved = scrollArea ? {
            minHeight: scrollArea.style.minHeight,
            overflowY: scrollArea.style.overflowY
        } : null;
        if (scrollArea) {
            scrollArea.style.minHeight = 'auto';
            scrollArea.style.overflowY = 'visible';
        }

        // scrollHeight umfasst die vollständige Content-Höhe inkl. overflow-Inhalt,
        // ohne dass wir max-height ändern müssen
        const height = container.scrollHeight;

        // Zurücksetzen
        contentElements.forEach((item, i) => {
            const el = item.el;
            Object.assign(el.style, savedStates[i]);
        });
        if (scrollArea && scrollAreaSaved) {
            scrollArea.style.minHeight = scrollAreaSaved.minHeight;
            scrollArea.style.overflowY = scrollAreaSaved.overflowY;
        }

        return height + 20; // Kleiner Puffer
    }

    /**
     * Expandiert den Container und blendet Content ein.
     * Unterstützt optional gleichzeitige Breiten-Animation auf dem äußeren Banner-Element.
     */
    expand(container, contentElements, options = {}) {
        this.cancel();
        this.isAnimating = true;

        const reducedMotion = prefersReducedMotion();
        const duration = reducedMotion ? 0 : (options.duration || 600);
        const contentDelay = reducedMotion ? 0 : (options.contentDelay || 0.4);
        const easing = options.easing || 'cubic-bezier(0.22, 1, 0.36, 1)';
        const onComplete = options.onComplete || null;
        const bannerElement = options.bannerElement || null;
        const targetWidth = options.targetWidth || null;
        const collapsedWidth = options.collapsedWidth || null;

        // SCHRITT 0 (ZUERST, bevor Content sichtbar gemacht wird):
        // CSS kann nicht von 'auto' oder 'none' animieren. Deshalb muss jetzt,
        // während der Content noch versteckt ist, die tatsächliche kollabierte Höhe
        // als konkreter Pixelwert gesetzt werden.
        const currentMaxHeight = container.style.maxHeight;
        if (!currentMaxHeight || currentMaxHeight === 'none' || currentMaxHeight === 'auto') {
            container.style.transition = 'none';
            container.style.maxHeight = container.scrollHeight + 'px'; // kollabierte Höhe (Content noch hidden)
            container.offsetHeight; // Reflow erzwingen um Wert zu committen
        }

        // 1. Content-Elemente vorbereiten (sichtbar aber transparent)
        contentElements.forEach(item => {
            const el = item.el;
            el.style.visibility = 'visible';
            el.style.maxHeight = 'none';
            el.style.overflow = 'visible';
            el.style.opacity = '0';
            el.style.transition = 'none';
            if (item.type === 'checkboxes') {
                el.style.display = 'flex';
            } else {
                el.style.display = 'block';
            }
            if (item.type === 'image') {
                const imageWidth = options.imageWidth || '80px';
                const imageHeight = options.imageHeight || '80px';
                el.style.width = imageWidth;
                el.style.height = imageHeight;
            }
        });

        // 2. Zielhöhe messen
        const targetHeight = this.measureExpandedHeight(container, contentElements);

        // Reflow erzwingen: Browser soll die wiederhergestellte collapsed-Höhe als
        // Ausgangspunkt für die Transition committen (sonst kann er ggf. 'none' als
        // Ausgangswert nehmen, was nicht animierbar wäre)
        container.offsetHeight; // eslint-disable-line no-unused-expressions

        // 3. Container-Transition setzen und expandieren
        container.style.transition = `max-height ${duration}ms ${easing}`;
        requestAnimationFrame(() => {
            container.style.maxHeight = targetHeight + 'px';
        });

        // 4. Breite des äußeren Banners animieren — NUR wenn sich kollabierte und
        //    normale Breite tatsächlich unterscheiden.
        if (bannerElement && targetWidth && collapsedWidth && targetWidth !== collapsedWidth) {
            bannerElement.offsetWidth; // eslint-disable-line no-unused-expressions
            bannerElement.style.transition = `width ${duration}ms ${easing}`;
            requestAnimationFrame(() => {
                bannerElement.style.width = targetWidth;
            });
        }

        // 5. Content einblenden nach contentDelay (überlappend mit Container)
        const contentStartMs = duration * contentDelay;
        const contentFadeDuration = reducedMotion ? 0 : duration * (1 - contentDelay) + 100; // Etwas länger als Rest

        const contentTimer = setTimeout(() => {
            contentElements.forEach((item, index) => {
                const el = item.el;
                // Gestaffelt: jedes Element 40ms nach dem vorherigen
                const stagger = reducedMotion ? 0 : index * 40;
                const itemTimer = setTimeout(() => {
                    el.style.transition = `opacity ${contentFadeDuration}ms ${easing}`;
                    el.style.opacity = '1';
                }, stagger);
                this._contentTimers.push(itemTimer);
            });
        }, contentStartMs);
        this._contentTimers.push(contentTimer);

        // 6. Aufräumen nach Abschluss
        const cleanupTimer = setTimeout(() => {
            this.isAnimating = false;
            if (onComplete) onComplete();
        }, duration + 100);
        this._contentTimers.push(cleanupTimer);
    }

    /**
     * Kollabiert den Container und blendet Content aus.
     * Unterstützt optional gleichzeitige Breiten-Animation auf dem äußeren Banner-Element.
     * Behandelt 'auto' als Ziel-Höhe korrekt: misst die natürliche kollabierte Höhe und
     * animiert zu einem konkreten Pixelwert, damit CSS-Transitions funktionieren.
     * Nach der Animation bleibt der konkrete Pixelwert erhalten (kein Reset auf 'none'),
     * damit expand() beim nächsten Aufruf davon weganimieren kann.
     */
    collapse(container, contentElements, collapsedHeight, options = {}) {
        this.cancel();
        this.isAnimating = true;

        const reducedMotion = prefersReducedMotion();
        const duration = reducedMotion ? 0 : (options.duration || 500);
        const easing = options.easing || 'cubic-bezier(0.22, 1, 0.36, 1)';
        const onComplete = options.onComplete || null;
        const contentFadeDuration = reducedMotion ? 0 : Math.min(duration * 0.4, 250);
        const bannerElement = options.bannerElement || null;
        const collapsedWidth = options.collapsedWidth || null;
        const expandedWidth = options.expandedWidth || null;

        // Wir messen IMMER die natürliche kollabierte Mindesthöhe (Titel + die
        // fixierten Buttons/Footer, die als flex-shrink:0-Geschwister im selben
        // Container stehen, siehe getBannerHTML()) — durch temporäres Verstecken
        // der expandable Elemente. Buttons und Footer können nicht schrumpfen und
        // dürfen deshalb NIE durch eine kleiner konfigurierte "Max Height
        // (Kollabiert)" abgeschnitten werden (container hat overflow:hidden).
        // Die konfigurierte Höhe wirkt daher nur noch als zusätzliche Grenze nach
        // oben, falls sie größer als die gemessene Mindesthöhe ist - nie als
        // hartes Cap darunter, sonst kollabiert das Banner auf eine unleserliche
        // Größe, die den fixierten Content abschneidet.
        const savedStyles = contentElements.map(item => ({
            display: item.el.style.display,
            maxHeight: item.el.style.maxHeight,
            overflow: item.el.style.overflow
        }));
        contentElements.forEach(item => {
            item.el.style.display = 'none';
        });
        const savedContainerMax = container.style.maxHeight;
        container.style.maxHeight = 'none';
        const measuredNatural = container.scrollHeight;
        // Wiederherstellen
        container.style.maxHeight = savedContainerMax;
        contentElements.forEach((item, i) => {
            item.el.style.display = savedStyles[i].display;
            item.el.style.maxHeight = savedStyles[i].maxHeight;
            item.el.style.overflow = savedStyles[i].overflow;
        });

        let targetCollapsedHeight = measuredNatural + 'px';
        const isAutoHeight = (!collapsedHeight || collapsedHeight === 'auto' || collapsedHeight === 'none');
        if (!isAutoHeight) {
            const configuredPx = parseFloat(collapsedHeight);
            if (!Number.isNaN(configuredPx) && /px\s*$/.test(String(collapsedHeight).trim()) && configuredPx > measuredNatural) {
                targetCollapsedHeight = configuredPx + 'px';
            }
        }

        // 1. Content ausblenden + Bild-Dimensionen gleichzeitig animieren
        contentElements.forEach(item => {
            const el = item.el;
            if (item.type === 'image') {
                // Bild: Opacity UND Dimensionen gleichzeitig auf 0 animieren
                el.style.transition = `opacity ${contentFadeDuration}ms ease-out, width ${contentFadeDuration}ms ease-out, height ${contentFadeDuration}ms ease-out`;
                el.style.opacity = '0';
                el.style.width = '0';
                el.style.height = '0';
            } else {
                el.style.transition = `opacity ${contentFadeDuration}ms ease-out`;
                el.style.opacity = '0';
            }
        });

        // 2. Nach Content-Fade: Container kollabieren + Breite animieren
        const collapseTimer = setTimeout(() => {
            const collapseDuration = duration * 0.6;

            // Force-reflow + RAF nötig, damit die CSS-Transition zuverlässig startet.
            // Transition-Eigenschaft synchron setzen, dann Wert im RAF ändern.
            container.offsetHeight; // eslint-disable-line no-unused-expressions
            container.style.transition = `max-height ${collapseDuration}ms ${easing}`;
            requestAnimationFrame(() => {
                container.style.maxHeight = targetCollapsedHeight;
            });

            // Breite des äußeren Banners animieren — NUR wenn sich die Breiten unterscheiden
            if (bannerElement && collapsedWidth && expandedWidth && collapsedWidth !== expandedWidth) {
                bannerElement.offsetWidth; // eslint-disable-line no-unused-expressions
                bannerElement.style.transition = `width ${collapseDuration}ms ${easing}`;
                requestAnimationFrame(() => {
                    bannerElement.style.width = collapsedWidth;
                });
            }
        }, contentFadeDuration);
        this._contentTimers.push(collapseTimer);

        // 3. Nach allem: Elemente vollständig ausblenden.
        // display:none entfernt das Element aus dem Layout (inkl. Margins) —
        // wichtig damit im kollabierten Zustand keine leeren Container übrig bleiben.
        const hideTimer = setTimeout(() => {
            contentElements.forEach(item => {
                const el = item.el;
                el.style.display = 'none';
                el.style.visibility = '';
                el.style.opacity = '';
                el.style.maxHeight = '';
                el.style.overflow = '';
                el.style.transition = '';
                el.style.position = '';
            });
            this.isAnimating = false;
            if (onComplete) onComplete();
        }, contentFadeDuration + duration * 0.6 + 50);
        this._contentTimers.push(hideTimer);
    }

    /**
     * Bricht laufende Animation sofort ab.
     */
    cancel() {
        this._contentTimers.forEach(t => clearTimeout(t));
        this._contentTimers = [];
        this._cleanupFns.forEach(fn => fn());
        this._cleanupFns = [];
        this.isAnimating = false;
    }
}

export class PixelmateBanner {
    // Eingebaute Standardtexte je Sprache - aber nur für die 9 mitgelieferten
    // Hauptsprachen (CORE_LANGUAGES). Sie dienen als Fallback für
    // Zusatzsprachen: Lässt der Betreiber ein Übersetzungsfeld im Repeater
    // leer, wird der passende Text in der jeweiligen Sprache angezeigt statt
    // der Hauptsprache. Die Texte für die Hauptsprachen sind zusätzlich mit
    // der WordPress-Übersetzung (.po/.mo) abgestimmt.
    //
    // Standardtexte für die übrigen 15 Amtssprachen der EU liegen NICHT hier
    // im Bundle, sondern in assets/public/js/i18n/banner-fallback-texts.json
    // (Quelle) - vom Client aus nie direkt geladen. Stattdessen liefert PHP
    // (DSGVO_Pixelmate_FrontendLocalizer::get_extended_language_defaults())
    // beim Seitenaufruf per pixelmateConfig.extendedLanguageDefaults nur die
    // Einträge, die für DIESE Website tatsächlich gebraucht werden
    // (konfigurierte Zusatzsprachen außerhalb von CORE_LANGUAGES, plus - bei
    // aktivierter Browsersprache-Autoerkennung - die per Accept-Language-
    // Header ermittelte Sprache). So bläht sich das Bundle nicht mit jeder
    // künftig hinzukommenden Sprache auf, obwohl die allermeisten Websites nur
    // eine Handvoll Sprachen tatsächlich nutzen. getLanguageDefaults() prüft
    // beide Quellen; Sprachen ohne eigene Übersetzung fallen auf Englisch zurück.
    static DEFAULT_TEXTS = {
        de: {
            title: 'Cookie-Einstellungen',
            text: 'Auf dieser Website setzen wir Technologien wie Cookies ein, um Ihnen ein reibungsloses Nutzungserlebnis zu bieten, Inhalte zu personalisieren und Zugriffe auf unsere Seiten zu analysieren. Hierbei verarbeiten wir auch personenbezogene Daten (z. B. IP-Adressen, Geräte-IDs oder Browserinformationen).\n\nEinzelne Kategorien und Dienste können unter „Einstellungen“ angepasst werden. Ihre Auswahl ist freiwillig und kann jederzeit mit Wirkung für die Zukunft über den fixierten „Cookies“ Link unten am Bildschirmrand angepasst oder widerrufen werden.\n\nManche der genutzten Dienste übermitteln Daten in Drittstaaten außerhalb der EU/des EWR und der Schweiz (beispielsweise in die USA). In diesen Ländern besteht unter Umständen ein geringeres Datenschutzniveau als in Ihrem Heimatland.\n\nWeitere Informationen finden Sie in unserer Datenschutzerklärung.',
            extendedText: 'Weitere Informationen finden Sie in unserer Datenschutzerklärung.',
            acceptButton: 'Alle akzeptieren',
            rejectButton: 'Alle ablehnen',
            saveSelectionButton: 'Auswahl speichern',
            cookieSettingsLink: 'Einstellungen',
            moreButton: 'Mehr anzeigen',
            lessButton: 'Weniger anzeigen',
            serviceDetailsLink: 'Mehr Informationen zu den eingesetzten Diensten',
            serviceDetailsLessLink: 'Details ausblenden',
            privacyLink: 'Datenschutz',
            imprintLink: 'Impressum',
            opensInNewTab: '(öffnet in neuem Tab)',
            consentAcceptedAnnouncement: 'Alle Cookies akzeptiert.',
            consentRejectedAnnouncement: 'Alle Cookies abgelehnt.',
            consentSavedAnnouncement: 'Cookie-Auswahl gespeichert.',
            required: 'Erforderlich',
            categories: {
                necessary: { name: 'Notwendig',      description: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich.' },
                analytics: { name: 'Statistik',      description: 'Diese Cookies helfen uns, die Nutzung der Website zu verstehen.' },
                marketing: { name: 'Marketing',      description: 'Diese Cookies werden für Werbung und Remarketing verwendet.' },
                external:  { name: 'Externe Medien', description: 'Diese Cookies erlauben das Laden externer Inhalte wie Videos und Karten.' }
            }
        },
        en: {
            title: 'Cookie Settings',
            text: 'On this website, we use technologies such as cookies to provide you with a smooth user experience, personalize content, and analyze traffic to our pages. In doing so, we also process personal data (e.g. IP addresses, device IDs, or browser information).\n\nIndividual categories and services can be adjusted under “Settings”. Your choice is voluntary and can be adjusted or revoked at any time with effect for the future via the fixed “Cookies” link at the bottom edge of the screen.\n\nSome of the services we use transfer data to third countries outside the EU/EEA and Switzerland (for example, to the USA). In these countries, the level of data protection may be lower than in your home country.\n\nYou can find more information in our privacy policy.',
            extendedText: 'You can find more information in our privacy policy.',
            acceptButton: 'Accept all',
            rejectButton: 'Reject all',
            saveSelectionButton: 'Save selection',
            cookieSettingsLink: 'Settings',
            moreButton: 'Show more',
            lessButton: 'Show less',
            serviceDetailsLink: 'More information about the services used',
            serviceDetailsLessLink: 'Hide details',
            privacyLink: 'Privacy',
            imprintLink: 'Imprint',
            opensInNewTab: '(opens in new tab)',
            consentAcceptedAnnouncement: 'All cookies accepted.',
            consentRejectedAnnouncement: 'All cookies rejected.',
            consentSavedAnnouncement: 'Cookie selection saved.',
            required: 'Required',
            categories: {
                necessary: { name: 'Necessary',        description: 'These cookies are required for the basic functions of the website.' },
                analytics: { name: 'Analytics',        description: 'These cookies help us understand how the website is used.' },
                marketing: { name: 'Marketing',        description: 'These cookies are used for advertising and remarketing.' },
                external:  { name: 'External Content', description: 'These cookies allow loading external content such as videos and maps.' }
            }
        },
        fr: {
            title: 'Paramètres des cookies',
            text: 'Sur ce site, nous utilisons des technologies telles que des cookies afin de vous offrir une expérience d\'utilisation fluide, de personnaliser les contenus et d\'analyser les accès à nos pages. Ce faisant, nous traitons également des données à caractère personnel (par ex. adresses IP, identifiants d\'appareils ou informations sur le navigateur).\n\nCertaines catégories et certains services peuvent être ajustés dans les « Paramètres ». Votre choix est volontaire et peut être modifié ou révoqué à tout moment, avec effet pour l\'avenir, via le lien « Cookies » fixe situé en bas de l\'écran.\n\nCertains des services utilisés transmettent des données vers des pays tiers situés en dehors de l\'UE/de l\'EEE et de la Suisse (par exemple vers les États-Unis). Dans ces pays, le niveau de protection des données peut être inférieur à celui de votre pays d\'origine.\n\nPour plus d\'informations, consultez notre politique de confidentialité.',
            extendedText: 'Pour plus d\'informations, consultez notre politique de confidentialité.',
            acceptButton: 'Tout accepter',
            rejectButton: 'Tout refuser',
            saveSelectionButton: 'Enregistrer la sélection',
            cookieSettingsLink: 'Paramètres',
            moreButton: 'Afficher plus',
            lessButton: 'Afficher moins',
            serviceDetailsLink: 'Plus d\'informations sur les services utilisés',
            serviceDetailsLessLink: 'Masquer les détails',
            privacyLink: 'Confidentialité',
            imprintLink: 'Mentions légales',
            opensInNewTab: '(s\'ouvre dans un nouvel onglet)',
            consentAcceptedAnnouncement: 'Tous les cookies ont été acceptés.',
            consentRejectedAnnouncement: 'Tous les cookies ont été refusés.',
            consentSavedAnnouncement: 'Sélection de cookies enregistrée.',
            required: 'Obligatoire',
            categories: {
                necessary: { name: 'Nécessaire',       description: 'Ces cookies sont nécessaires aux fonctions de base du site.' },
                analytics: { name: 'Analyse',          description: 'Ces cookies nous aident à comprendre l\'utilisation du site.' },
                marketing: { name: 'Marketing',        description: 'Ces cookies sont utilisés pour la publicité et le remarketing.' },
                external:  { name: 'Médias externes',  description: 'Ces cookies permettent de charger du contenu externe tel que des vidéos et des cartes.' }
            }
        },
        es: {
            title: 'Configuración de cookies',
            text: 'En este sitio web utilizamos tecnologías como las cookies para ofrecerle una experiencia de uso fluida, personalizar contenidos y analizar los accesos a nuestras páginas. Para ello, también tratamos datos de carácter personal (p. ej., direcciones IP, identificadores de dispositivo o información del navegador).\n\nAlgunas categorías y servicios pueden ajustarse en «Ajustes». Su elección es voluntaria y puede modificarse o revocarse en cualquier momento, con efectos para el futuro, a través del enlace fijo «Cookies» situado en la parte inferior de la pantalla.\n\nAlgunos de los servicios utilizados transmiten datos a terceros países fuera de la UE/EEE y de Suiza (por ejemplo, a EE. UU.). En estos países puede existir, en determinadas circunstancias, un nivel de protección de datos inferior al de su país de origen.\n\nPara más información, consulte nuestra política de privacidad.',
            extendedText: 'Para más información, consulte nuestra política de privacidad.',
            acceptButton: 'Aceptar todo',
            rejectButton: 'Rechazar todo',
            saveSelectionButton: 'Guardar selección',
            cookieSettingsLink: 'Ajustes',
            moreButton: 'Mostrar más',
            lessButton: 'Mostrar menos',
            serviceDetailsLink: 'Más información sobre los servicios utilizados',
            serviceDetailsLessLink: 'Ocultar detalles',
            privacyLink: 'Privacidad',
            imprintLink: 'Aviso legal',
            opensInNewTab: '(se abre en una pestaña nueva)',
            consentAcceptedAnnouncement: 'Se han aceptado todas las cookies.',
            consentRejectedAnnouncement: 'Se han rechazado todas las cookies.',
            consentSavedAnnouncement: 'Selección de cookies guardada.',
            required: 'Obligatorio',
            categories: {
                necessary: { name: 'Necesario',      description: 'Estas cookies son necesarias para las funciones básicas del sitio web.' },
                analytics: { name: 'Estadísticas',   description: 'Estas cookies nos ayudan a comprender el uso del sitio web.' },
                marketing: { name: 'Marketing',      description: 'Estas cookies se utilizan para publicidad y remarketing.' },
                external:  { name: 'Medios externos', description: 'Estas cookies permiten cargar contenido externo, como vídeos y mapas.' }
            }
        },
        it: {
            title: 'Impostazioni dei cookie',
            text: 'Su questo sito web utilizziamo tecnologie come i cookie per offrirle un\'esperienza d\'uso fluida, personalizzare i contenuti e analizzare gli accessi alle nostre pagine. In questo contesto trattiamo anche dati personali (ad es. indirizzi IP, ID del dispositivo o informazioni del browser).\n\nAlcune categorie e servizi possono essere modificati in «Impostazioni». La sua scelta è volontaria e può essere modificata o revocata in qualsiasi momento, con effetto per il futuro, tramite il link fisso «Cookies» nella parte inferiore dello schermo.\n\nAlcuni dei servizi utilizzati trasmettono dati a paesi terzi al di fuori dell\'UE/SEE e della Svizzera (ad esempio verso gli Stati Uniti). In questi paesi il livello di protezione dei dati potrebbe essere inferiore rispetto a quello del proprio paese d\'origine.\n\nUlteriori informazioni sono disponibili nella nostra informativa sulla privacy.',
            extendedText: 'Ulteriori informazioni sono disponibili nella nostra informativa sulla privacy.',
            acceptButton: 'Accetta tutto',
            rejectButton: 'Rifiuta tutto',
            saveSelectionButton: 'Salva selezione',
            cookieSettingsLink: 'Impostazioni',
            moreButton: 'Mostra di più',
            lessButton: 'Mostra di meno',
            serviceDetailsLink: 'Ulteriori informazioni sui servizi utilizzati',
            serviceDetailsLessLink: 'Nascondi dettagli',
            privacyLink: 'Privacy',
            imprintLink: 'Note legali',
            opensInNewTab: '(si apre in una nuova scheda)',
            consentAcceptedAnnouncement: 'Tutti i cookie sono stati accettati.',
            consentRejectedAnnouncement: 'Tutti i cookie sono stati rifiutati.',
            consentSavedAnnouncement: 'Selezione dei cookie salvata.',
            required: 'Necessario',
            categories: {
                necessary: { name: 'Necessari',      description: 'Questi cookie sono necessari per le funzioni di base del sito web.' },
                analytics: { name: 'Statistiche',    description: 'Questi cookie ci aiutano a comprendere l\'utilizzo del sito web.' },
                marketing: { name: 'Marketing',      description: 'Questi cookie vengono utilizzati per pubblicità e remarketing.' },
                external:  { name: 'Media esterni',  description: 'Questi cookie consentono di caricare contenuti esterni come video e mappe.' }
            }
        },
        nl: {
            title: 'Cookie-instellingen',
            text: 'Op deze website gebruiken wij technologieën zoals cookies om u een soepele gebruikservaring te bieden, inhoud te personaliseren en bezoeken aan onze pagina\'s te analyseren. Hierbij verwerken wij ook persoonsgegevens (bijv. IP-adressen, apparaat-ID\'s of browserinformatie).\n\nAfzonderlijke categorieën en diensten kunnen worden aangepast onder „Instellingen”. Uw keuze is vrijwillig en kan te allen tijde met werking voor de toekomst worden aangepast of ingetrokken via de vaste link „Cookies” onderaan het scherm.\n\nSommige van de gebruikte diensten geven gegevens door aan derde landen buiten de EU/EER en Zwitserland (bijvoorbeeld naar de VS). In deze landen kan mogelijk een lager niveau van gegevensbescherming gelden dan in uw eigen land.\n\nMeer informatie vindt u in ons privacybeleid.',
            extendedText: 'Meer informatie vindt u in ons privacybeleid.',
            acceptButton: 'Alles accepteren',
            rejectButton: 'Alles weigeren',
            saveSelectionButton: 'Selectie opslaan',
            cookieSettingsLink: 'Cookie-instellingen',
            moreButton: 'Meer weergeven',
            lessButton: 'Minder weergeven',
            serviceDetailsLink: 'Meer informatie over de gebruikte diensten',
            serviceDetailsLessLink: 'Details verbergen',
            privacyLink: 'Privacy',
            imprintLink: 'Colofon',
            opensInNewTab: '(opent in nieuw tabblad)',
            consentAcceptedAnnouncement: 'Alle cookies geaccepteerd.',
            consentRejectedAnnouncement: 'Alle cookies geweigerd.',
            consentSavedAnnouncement: 'Cookie-selectie opgeslagen.',
            required: 'Verplicht',
            categories: {
                necessary: { name: 'Noodzakelijk',   description: 'Deze cookies zijn noodzakelijk voor de basisfuncties van de website.' },
                analytics: { name: 'Statistieken',   description: 'Deze cookies helpen ons te begrijpen hoe de website wordt gebruikt.' },
                marketing: { name: 'Marketing',      description: 'Deze cookies worden gebruikt voor reclame en remarketing.' },
                external:  { name: 'Externe media',  description: 'Deze cookies maken het laden van externe inhoud zoals video\'s en kaarten mogelijk.' }
            }
        },
        pl: {
            title: 'Ustawienia plików cookie',
            text: 'Na tej stronie internetowej wykorzystujemy technologie takie jak pliki cookie, aby zapewnić Państwu płynne korzystanie z witryny, personalizować treści oraz analizować sposób korzystania z naszych stron. W ten sposób przetwarzamy również dane osobowe (np. adresy IP, identyfikatory urządzeń lub informacje o przeglądarce).\n\nPoszczególne kategorie i usługi można dostosować w „Ustawieniach”. Wybór jest dobrowolny i może zostać w każdej chwili zmieniony lub odwołany ze skutkiem na przyszłość poprzez stały link „Cookies” w dolnej części ekranu.\n\nNiektóre z wykorzystywanych usług przekazują dane do państw trzecich spoza UE/EOG i Szwajcarii (na przykład do USA). W krajach tych poziom ochrony danych może być niższy niż w Państwa kraju.\n\nWięcej informacji znajdą Państwo w naszej polityce prywatności.',
            extendedText: 'Więcej informacji znajdą Państwo w naszej polityce prywatności.',
            acceptButton: 'Zaakceptuj wszystkie',
            rejectButton: 'Odrzuć wszystkie',
            saveSelectionButton: 'Zapisz wybór',
            cookieSettingsLink: 'Ustawienia plików cookie',
            moreButton: 'Pokaż więcej',
            lessButton: 'Pokaż mniej',
            serviceDetailsLink: 'Więcej informacji o wykorzystywanych usługach',
            serviceDetailsLessLink: 'Ukryj szczegóły',
            privacyLink: 'Prywatność',
            imprintLink: 'Nota prawna',
            opensInNewTab: '(otwiera się w nowej karcie)',
            consentAcceptedAnnouncement: 'Zaakceptowano wszystkie pliki cookie.',
            consentRejectedAnnouncement: 'Odrzucono wszystkie pliki cookie.',
            consentSavedAnnouncement: 'Wybór plików cookie zapisany.',
            required: 'Wymagane',
            categories: {
                necessary: { name: 'Niezbędne',        description: 'Te pliki cookie są niezbędne do podstawowych funkcji witryny.' },
                analytics: { name: 'Statystyka',       description: 'Te pliki cookie pomagają nam zrozumieć sposób korzystania z witryny.' },
                marketing: { name: 'Marketing',        description: 'Te pliki cookie są wykorzystywane do reklamy i remarketingu.' },
                external:  { name: 'Media zewnętrzne', description: 'Te pliki cookie umożliwiają ładowanie treści zewnętrznych, takich jak filmy i mapy.' }
            }
        },
        da: {
            title: 'Cookieindstillinger',
            text: 'På dette website anvender vi teknologier som cookies for at give dig en gnidningsfri brugeroplevelse, personalisere indhold og analysere adgangen til vores sider. I den forbindelse behandler vi også personoplysninger (f.eks. IP-adresser, enheds-id\'er eller browseroplysninger).\n\nEnkelte kategorier og tjenester kan tilpasses under „Indstillinger”. Dit valg er frivilligt og kan til enhver tid ændres eller tilbagekaldes med fremtidig virkning via det faste link „Cookies” nederst på skærmen.\n\nNogle af de anvendte tjenester overfører data til tredjelande uden for EU/EØS og Schweiz (for eksempel til USA). I disse lande kan der være et lavere databeskyttelsesniveau end i dit hjemland.\n\nYderligere oplysninger findes i vores privatlivspolitik.',
            extendedText: 'Yderligere oplysninger findes i vores privatlivspolitik.',
            acceptButton: 'Accepter alle',
            rejectButton: 'Afvis alle',
            saveSelectionButton: 'Gem valg',
            cookieSettingsLink: 'Cookieindstillinger',
            moreButton: 'Vis mere',
            lessButton: 'Vis mindre',
            serviceDetailsLink: 'Mere information om de anvendte tjenester',
            serviceDetailsLessLink: 'Skjul detaljer',
            privacyLink: 'Privatliv',
            imprintLink: 'Kolofon',
            opensInNewTab: '(åbner i en ny fane)',
            consentAcceptedAnnouncement: 'Alle cookies accepteret.',
            consentRejectedAnnouncement: 'Alle cookies afvist.',
            consentSavedAnnouncement: 'Cookie-valg gemt.',
            required: 'Påkrævet',
            categories: {
                necessary: { name: 'Nødvendige',     description: 'Disse cookies er nødvendige for webstedets grundlæggende funktioner.' },
                analytics: { name: 'Statistik',      description: 'Disse cookies hjælper os med at forstå, hvordan webstedet bruges.' },
                marketing: { name: 'Marketing',      description: 'Disse cookies bruges til reklame og remarketing.' },
                external:  { name: 'Eksternt indhold', description: 'Disse cookies gør det muligt at indlæse eksternt indhold som videoer og kort.' }
            }
        },
        sv: {
            title: 'Cookie-inställningar',
            text: 'På denna webbplats använder vi tekniker som cookies för att ge dig en smidig användarupplevelse, anpassa innehåll och analysera hur våra sidor används. I samband med detta behandlar vi även personuppgifter (t.ex. IP-adresser, enhets-ID eller webbläsarinformation).\n\nEnskilda kategorier och tjänster kan justeras under ”Inställningar”. Ditt val är frivilligt och kan när som helst ändras eller återkallas med verkan för framtiden via den fasta länken ”Cookies” längst ned på skärmen.\n\nVissa av de tjänster vi använder överför uppgifter till tredjeländer utanför EU/EES och Schweiz (till exempel till USA). I dessa länder kan skyddsnivån för personuppgifter i vissa fall vara lägre än i ditt hemland.\n\nMer information finns i vår integritetspolicy.',
            extendedText: 'Mer information finns i vår integritetspolicy.',
            acceptButton: 'Acceptera alla',
            rejectButton: 'Avvisa alla',
            saveSelectionButton: 'Spara val',
            cookieSettingsLink: 'Cookie-inställningar',
            moreButton: 'Visa mer',
            lessButton: 'Visa mindre',
            serviceDetailsLink: 'Mer information om de tjänster som används',
            serviceDetailsLessLink: 'Dölj detaljer',
            privacyLink: 'Integritet',
            imprintLink: 'Om webbplatsen',
            opensInNewTab: '(öppnas i en ny flik)',
            consentAcceptedAnnouncement: 'Alla cookies har godkänts.',
            consentRejectedAnnouncement: 'Alla cookies har avvisats.',
            consentSavedAnnouncement: 'Cookie-val har sparats.',
            required: 'Obligatorisk',
            categories: {
                necessary: { name: 'Nödvändiga',      description: 'Dessa cookies är nödvändiga för webbplatsens grundläggande funktioner.' },
                analytics: { name: 'Statistik',       description: 'Dessa cookies hjälper oss att förstå hur webbplatsen används.' },
                marketing: { name: 'Marknadsföring',  description: 'Dessa cookies används för annonsering och remarketing.' },
                external:  { name: 'Externt innehåll', description: 'Dessa cookies möjliggör laddning av externt innehåll som videor och kartor.' }
            }
        }
    };

    // Hauptsprachen (de/en/fr/es/it/nl/pl/da/sv): für sie gilt ausschließlich die
    // reguläre Spracherkennung (WPML/Polylang bzw. WordPress-Sprache) - die
    // automatische Erkennung der Browsersprache
    // (pixelmate_browser_language_autodetect) greift nur für die übrigen,
    // mitgelieferten Zusatzsprachen.
    static CORE_LANGUAGES = ['de', 'en', 'fr', 'es', 'it', 'nl', 'pl', 'da', 'sv'];

    // Locale-Codes der mitgelieferten Hauptsprachen - im Footer-Sprach-
    // Umschalter (siehe getLanguageSwitcherHTML()) und im Text-Cache (siehe
    // initializeTextCache()) immer zusätzlich zur konfigurierten Hauptsprache
    // und zu benutzerdefinierten Zusatzsprachen wählbar, unabhängig davon, ob
    // sie vom Admin explizit als Zusatzsprache angelegt wurden.
    static CORE_LANGUAGE_LOCALES = {
        de: 'de_DE',
        en: 'en_US',
        fr: 'fr_FR',
        es: 'es_ES',
        it: 'it_IT',
        nl: 'nl_NL',
        pl: 'pl_PL',
        da: 'da_DK',
        sv: 'sv_SE'
    };

    constructor() {
        this.config = window.pixelmateConfig || {};
        // Flache Kopie statt Referenz: this.settings.language wird weiter unten
        // (Browser-Spracherkennung) und beim manuellen Sprachwechsel überschrieben.
        // Eine Kopie verhindert, dass das die eigentliche Konfiguration
        // (this.config.settings.language, z.B. für den Sprach-Umschalter oder das
        // Settings Modal) mit verändert.
        this.settings = { ...(this.config.settings || {}) };
        this.design = this.config.design || {};
        this.cookies = this.config.cookies || {};
        this.urls = this.config.urls || {};
        this.csp = this.config.csp || {};
        this.settings.language = this.settings.language || 'de_DE';

        // Browsersprache automatisch erkennen (nur Zusatzsprachen - siehe
        // pixelmate_browser_language_autodetect). Die Hauptsprachen de/en/fr/es/it
        // bleiben unberührt: für sie gilt weiterhin die reguläre Ermittlung über
        // WPML/Polylang bzw. die WordPress-Sprache (this.config.settings.language).
        if (this.settings.browserLanguageAutodetect) {
            this.applyBrowserLanguageAutodetect();
        }

        // Text-Cache für alle verfügbaren Sprachen
        this.textCache = {};
        this.initializeTextCache();

        // Aktuelle Texte setzen (für Kompatibilität mit bestehenden Methoden)
        this.texts = this.textCache[this.settings.language] || this.getDefaultTexts();

        // Referenzen auf DOM-Elemente für schnelle Updates
        this.titleElement = null;
        this.textElement = null;
        this.extendedTextElement = null;
        this.buttonElements = {};
        this.moreButtonElement = null;
        this.saveSelectionButtonElement = null;
        this.categoryCheckboxes = {};

        this.bannerElement = null;
        this.overlayElement = null;
        this.isVisible = false;
        this.isExpanded = false;

        // Hover-Timeout für expandable_hover Modus
        this.hoverTimeout = null;

        // Cookie-Namen
        this.cookieName = this.cookies.cookieName || 'pixelmate_consent';
        this.cookiePrefix = this.cookies.cookiePrefix || 'pixelmate_';

        // Versions-Kennung der aktuellen einwilligungsrelevanten Konfiguration.
        // Weicht ein gespeicherter Consent hiervon ab (neue Dienste/Zwecke), wird
        // erneut um Einwilligung gebeten (Re-Consent).
        this.consentVersion = this.config.consentVersion || '';

        // Script Blocker initialisieren
        this.scriptBlocker = new ScriptBlocker();

        // Settings Modal initialisieren
        this.settingsModal = new SettingsModal(this);

        // Content Blocker initialisieren (für YouTube etc.)
        this.contentBlocker = new ContentBlocker(this);

        // Tracking Data Preserver initialisieren
        this.trackingDataPreserver = new TrackingDataPreserver();

        // Cloudflare Turnstile Formular-Schutz initialisieren
        this.turnstileFormGuard = new TurnstileFormGuard(this);

        // Google reCAPTCHA Formular-Schutz initialisieren
        this.recaptchaFormGuard = new RecaptchaFormGuard(this);

        this.init();
    }

    init() {
        this.logDebugInfo();
        this.watchCspViolations();

        // Tracking-Daten nach Reload wiederherstellen (falls vorhanden)
        this.trackingDataPreserver.restoreTrackingData();

        // Fixierter Widerruf-Button: unabhängig vom Consent-Status verfügbar,
        // damit die Einwilligung jederzeit widerrufen/geändert werden kann.
        this.initFloatingRevokeButton();

        // Prüfen ob bereits eine Entscheidung getroffen wurde.
        // window.pixelmateEarlyConsent wird vom Inline-Script im <head> gesetzt wenn
        // bereits ein gültiger Consent-Cookie existiert — spart das erneute Parsen.
        const existingConsent = window.pixelmateEarlyConsent || this.getCookie(this.cookieName);

        if (existingConsent && this.isConsentCurrent(existingConsent)) {
            // Gültiger, aktueller Consent vorhanden → Scripts aktivieren, Banner nicht zeigen
            pmLog('Existing consent found:', existingConsent);
            this.scriptBlocker.loadConsent(existingConsent);
            this.contentBlocker.loadConsent(existingConsent);
            return;
        }

        // Global Privacy Control (GPC): Sendet der Browser ein gültiges GPC-Signal
        // (navigator.globalPrivacyControl === true oder serverseitig erkannter
        // Sec-GPC: 1 Header), behandeln wir dies als verbindliche Ablehnung aller
        // nicht notwendigen Dienste — ohne den Nutzer mit einem Banner zu behelligen.
        // Siehe https://w3c.github.io/gpc/ und Art. 21(5) DSGVO.
        if (this.isGpcSignalActive()) {
            this.applyGpcConsent();
            return;
        }

        // Do Not Track (DNT): Sendet der Browser ein DNT-Signal (navigator.doNotTrack
        // bzw. serverseitig erkannter DNT: 1 Header), behandeln wir dies ebenfalls als
        // Ablehnung aller nicht notwendigen Dienste. DNT wurde vom W3C nie final
        // standardisiert und ist damit rechtlich weniger verbindlich als GPC, eine
        // Ablehnung ist als "Weniger als Einwilligung" aber in jedem Fall unbedenklich.
        if (this.isDntSignalActive()) {
            this.applyDntConsent();
            return;
        }

        // Kein Consent → Banner anzeigen und Content blockieren
        this.contentBlocker.init();

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.showBanner());
        } else {
            this.showBanner();
        }
    }

    /**
     * Gibt bei aktivem Debug-Modus einmalig Plugin-/Theme-/Plugin-Umgebung in die
     * Konsole aus. Dient dazu, bei Support-Anfragen schnell Versions- und
     * Plugin-Konflikte einzugrenzen, ohne extra nachfragen zu müssen.
     */
    logDebugInfo() {
        const info = this.config.debugInfo;
        if (!info) {
            return;
        }
        pmLog('Umgebung:', info);
    }

    /**
     * Meldet CSP-Verstöße (blockierte Ressourcen) über die Browser-Konsole.
     * Der häufigste Grund für "Dienst X funktioniert nicht" bei Kunden ist ein
     * Theme/Plugin, das eine Ressource lädt, die von Pixelmates Content-Security-
     * Policy blockiert wird - mit diesem Listener ist das sofort sichtbar.
     */
    watchCspViolations() {
        if (typeof document.addEventListener !== 'function') {
            return;
        }
        document.addEventListener('securitypolicyviolation', (e) => {
            pmWarn(
                `CSP blockiert: ${e.blockedURI} (Direktive: ${e.violatedDirective})`,
                { sourceFile: e.sourceFile, lineNumber: e.lineNumber }
            );
        });
    }

    /**
     * Prüft, ob ein gespeicherter Consent noch zur aktuellen Konfiguration passt.
     *
     * Stimmt die im Cookie hinterlegte Versions-Kennung nicht mehr mit der aktuellen
     * `consentVersion` überein (z. B. weil ein neuer Dienst/Zweck hinzugekommen ist
     * oder ein älterer Consent noch ohne Versionsfeld gespeichert wurde), gilt die
     * Einwilligung als überholt und es muss erneut um Zustimmung gebeten werden.
     *
     * Ist serverseitig keine Versionierung konfiguriert (leerer Wert), bleibt das
     * bisherige Verhalten erhalten und ein vorhandener Consent wird akzeptiert.
     *
     * @param {Object} consent Das geparste Consent-Cookie.
     * @returns {boolean}
     */
    isConsentCurrent(consent) {
        if (!consent) {
            return false;
        }
        if (!this.consentVersion) {
            return true;
        }
        return consent.version === this.consentVersion;
    }

    /**
     * Prüft, ob ein aktives Global-Privacy-Control-Signal vorliegt.
     *
     * Erkennt das Signal auf zwei Ebenen:
     *   1. Client: navigator.globalPrivacyControl === true (JS-API)
     *   2. Server: config.gpc === true (serverseitig erkannter Sec-GPC: 1 Header)
     *
     * Die Auswertung kann über die Einstellung gpcEnabled deaktiviert werden
     * (Standard: aktiviert).
     */
    isGpcSignalActive() {
        // GPC-Auswertung per Einstellung deaktivierbar (Default: aktiviert).
        if (this.settings.gpcEnabled === false) {
            return false;
        }

        const clientSignal = typeof navigator !== 'undefined'
            && navigator.globalPrivacyControl === true;
        const serverSignal = this.config.gpc === true;

        return clientSignal || serverSignal;
    }

    /**
     * Wendet eine durch ein GPC-Signal ausgelöste Ablehnung an: alle nicht
     * notwendigen Kategorien werden abgelehnt, der Consent wird persistiert
     * (markiert mit gpc: true) und externe Inhalte werden blockiert. Es wird
     * kein Banner angezeigt.
     */
    applyGpcConsent() {
        pmLog('GPC signal detected → rejecting all non-essential services');

        const categories = this.scriptBlocker.rejectAll();
        this.setConsent('reject', categories, { gpc: true });

        // Externe Inhalte (YouTube etc.) weiterhin blockieren
        this.contentBlocker.init();
    }

    /**
     * Prüft, ob ein aktives Do-Not-Track-Signal vorliegt.
     *
     * Erkennt das Signal auf zwei Ebenen:
     *   1. Client: navigator.doNotTrack === "1" (Firefox/Safari) bzw. "yes" (ältere
     *      IE-Versionen) oder window.doNotTrack === "1" (ältere Chrome-Versionen)
     *   2. Server: config.dnt === true (serverseitig erkannter DNT: 1 Header)
     *
     * Die Auswertung kann über die Einstellung dntEnabled deaktiviert werden
     * (Standard: aktiviert).
     */
    isDntSignalActive() {
        // DNT-Auswertung per Einstellung deaktivierbar (Default: aktiviert).
        if (this.settings.dntEnabled === false) {
            return false;
        }

        const nav = typeof navigator !== 'undefined' ? navigator : null;
        const clientSignal = !!nav && (nav.doNotTrack === '1' || nav.doNotTrack === 'yes')
            || (typeof window !== 'undefined' && window.doNotTrack === '1');
        const serverSignal = this.config.dnt === true;

        return clientSignal || serverSignal;
    }

    /**
     * Wendet eine durch ein DNT-Signal ausgelöste Ablehnung an: alle nicht
     * notwendigen Kategorien werden abgelehnt, der Consent wird persistiert
     * (markiert mit dnt: true) und externe Inhalte werden blockiert. Es wird
     * kein Banner angezeigt.
     */
    applyDntConsent() {
        pmLog('DNT signal detected → rejecting all non-essential services');

        const categories = this.scriptBlocker.rejectAll();
        this.setConsent('reject', categories, { dnt: true });

        // Externe Inhalte (YouTube etc.) weiterhin blockieren
        this.contentBlocker.init();
    }

    initializeTextCache() {
        // Hauptsprache cachen
        const mainLanguage = this.config.settings?.language || 'de_DE';
        this.textCache[mainLanguage] = this.getTextsForLanguage(mainLanguage);

        // Zusätzliche Sprachen cachen
        const additionalLanguages = this.config.settings?.additionalLanguages || [];
        additionalLanguages.forEach(lang => {
            if (lang.language_code && lang.language_code !== mainLanguage) {
                this.textCache[lang.language_code] = this.getTextsForLanguage(lang.language_code);
            }
        });

        // Mitgelieferte Hauptsprachen (DE/EN/FR/ES/IT/NL/PL/DA) immer cachen,
        // damit der Sprach-Umschalter im Footer (siehe getLanguageSwitcherHTML())
        // für sie funktioniert, auch wenn sie nicht als Zusatzsprache konfiguriert
        // wurden.
        Object.values(PixelmateBanner.CORE_LANGUAGE_LOCALES).forEach(code => {
            if (!this.textCache[code]) {
                this.textCache[code] = this.getTextsForLanguage(code);
            }
        });

        // Per Browsersprache automatisch erkannte Zusatzsprache: eigener
        // Cache-Eintrag, auch wenn sie nicht manuell als Zusatzsprache
        // hinterlegt wurde (getTextsForLanguage() liefert dafür bereits die
        // eingebauten Standardtexte zurück).
        if (this.settings.language && !this.textCache[this.settings.language]) {
            this.textCache[this.settings.language] = this.getTextsForLanguage(this.settings.language);
        }

        pmLog('Text cache initialized:', this.textCache);
    }

    showBanner() {
        // On legal pages (privacy policy, imprint) the banner must not be shown;
        // the floating revoke button (if enabled) is still initialised in init().
        if (!this.settings.showBanner || this.isVisible || this.settings.isLegalPage) {
            return;
        }

        const delay = this.settings.delayedLoading * 1000;

        if (delay > 0) {
            setTimeout(() => this.createBanner(), delay);
        } else {
            this.createBanner();
        }
    }

    createBanner() {
        // Overlay erstellen falls gewünscht
        if (this.design.overlay && this.design.overlay.type !== 'none') {
            this.createOverlay();
        }

        // Banner Element erstellen
        this.bannerElement = document.createElement('div');
        this.bannerElement.id = 'pixelmate-banner';
        // "pixelmate-banner-bottom" ist die Klasse der Legacy-Plugin-Version.
        // Sie bleibt als Alias erhalten, damit Custom-CSS, das Kunden vor der
        // Migration eingefügt haben, nach dem Update weiterhin greift.
        this.bannerElement.className = 'pixelmate-banner pixelmate-banner-bottom';
        this.bannerElement.setAttribute('role', 'dialog');
        // Bewusst KEIN aria-modal: der Banner sperrt die Seite nicht (kein Cookie-Wall,
        // das Overlay ist pointer-events:none). aria-modal="true" würde Screenreadern
        // das Gegenteil melden und den restlichen Seiteninhalt für sie ausblenden.
        // Aus demselben Grund kein aria-live — die Ansage übernimmt der Fokuswechsel
        // in focusBanner(), sonst wird der Inhalt doppelt vorgelesen.
        if (this.texts.title) {
            this.bannerElement.setAttribute('aria-labelledby', 'pixelmate-banner-title');
        } else {
            this.bannerElement.setAttribute('aria-label', this.texts.cookieSettingsLink || 'Einstellungen');
        }
        // Erlaubt das programmatische Fokussieren, ohne den Banner in die Tab-Reihenfolge
        // aufzunehmen.
        this.bannerElement.setAttribute('tabindex', '-1');

        // lang-Attribut (WCAG 3.1.1/3.1.2): der Banner-Inhalt kann von der
        // Sprache der umgebenden Seite (<html lang>) abweichen - z.B. durch
        // Browsersprache-Autoerkennung oder den manuellen Sprachumschalter.
        // Ohne eigenes lang-Attribut würde ein Screenreader den Inhalt sonst
        // mit der (falschen) Sprach-Engine der Seite vorlesen. Wird bei jedem
        // Sprachwechsel in updateTextElements() aktualisiert.
        this.bannerElement.setAttribute('lang', PixelmateBanner.resolveDefaultTextsLang(this.settings.language));

        // Inline Styles für Basis-Layout
        this.applyBannerStyles();

        // Custom CSS hinzufügen falls definiert
        this.injectCustomCSS();

        // Sichtbare Fokus-Markierung für Tastaturbedienung
        this.injectA11yStyles();

        // Banner Inhalt erstellen
        this.bannerElement.innerHTML = this.getBannerHTML();

        // Banner zum DOM hinzufügen
        document.body.appendChild(this.bannerElement);

        // Event Listeners hinzufügen
        this.attachEventListeners();

        // Animation
        this.animateIn();

        this.isVisible = true;

        // Fokus in den Banner setzen, damit Tastatur- und Screenreader-Nutzer ihn
        // erreichen und angesagt bekommen. Ohne das müssten sie sich erst durch die
        // gesamte Seite tabben, um an die Einwilligungs-Buttons zu kommen.
        this.focusBanner();
    }

    /**
     * Setzt den Erst-Fokus beim Erscheinen des Banners, damit Tastatur- und
     * Screenreader-Nutzer ihn direkt erreichen, statt sich erst durch die ganze
     * Seite tabben zu müssen.
     *
     * Standardmäßig (settings.focusRejectButton = false) landet der Fokus auf der
     * gesamten Banner-Box - das entspricht dem in BITV/EN 301 549 verlangten
     * neutralen Verhalten, da dabei kein Button gegenüber den anderen bevorzugt
     * wird; Tastaturnutzer tabben sich von dort aus selbst zum gewünschten Button.
     * Optional (Haupteinstellungen) kann der Fokus stattdessen direkt auf den
     * "Alle ablehnen"-Button gesetzt werden, um datenschutzfreundliche Bedienung
     * mit einem Tastendruck weniger zu ermöglichen. Ist der Button in diesem Fall
     * (noch) nicht sichtbar - z. B. weil die Buttons erst nach dem Erweitern
     * erscheinen - fällt der Fokus auf den Banner-Container zurück.
     *
     * preventScroll verhindert, dass der Browser die Seite zum Banner scrollt — er
     * ist ohnehin fixiert positioniert und damit bereits sichtbar.
     */
    focusBanner() {
        if (!this.bannerElement) return;

        const rejectButton = this.buttonElements?.reject;
        const focusRejectButton = !!this.settings?.focusRejectButton;
        const target = (focusRejectButton && rejectButton && rejectButton.offsetParent !== null)
            ? rejectButton
            : this.bannerElement;

        // Verhindert, dass dieser programmatische Erst-Fokus (Barrierefreiheit)
        // im Hover-Expand-Modus das Banner beim Laden fälschlich aufklappt - dort
        // expandiert setupSmoothHoverAnimation() sonst auch bei jedem
        // "focusin", nicht nur bei echtem Hover/Tab durch den Besucher.
        this._suppressNextFocusExpand = true;

        try {
            target.focus({ preventScroll: true });
        } catch (e) {
            // Ältere Browser kennen die Options-Form von focus() nicht.
            target.focus();
        }
    }

    /**
     * Fügt die Fokus-Markierung für Banner, Widerruf-Button und Einstellungs-Modal ein.
     *
     * Der Banner stylt sich sonst vollständig über Inline-Styles; ein :focus-visible-
     * Zustand lässt sich so aber nicht abbilden. Ohne sichtbare Markierung ist für
     * Tastaturnutzer nicht erkennbar, wo sie sich gerade befinden (WCAG 2.4.7).
     *
     * Die versteckten Checkboxen der Schalter im Modal (opacity:0, 0x0 px) sind zwar
     * fokussierbar, aber unsichtbar — deshalb wandert der Ring dort auf das sichtbare
     * Slider-Element daneben.
     *
     * Die Ringfarbe ist bewusst ein fest verdrahtetes, sanftes Blau statt der
     * (von Website zu Website wechselnden) Akzentfarbe der Buttons: Ein
     * Fokusindikator muss unabhängig vom individuellen Farbschema zuverlässig als
     * "hier ist der Fokus" erkennbar sein - je nach gewählter Akzentfarbe (z. B.
     * Rot, Grün) ließe sich das sonst leicht mit einem Fehler- oder Erfolgszustand
     * verwechseln.
     */
    injectA11yStyles() {
        if (document.getElementById('pixelmate-a11y-css')) {
            return;
        }

        const accent = '#4d90d9';
        // "Verstärkter Fokus-Rahmen" (Barrierefreiheits-Einstellung): breiterer,
        // weiter abgesetzter Ring statt des schmalen Standard-Rings - erleichtert
        // sehbehinderten Tastaturnutzern das Erkennen des Fokus (WCAG 2.4.7/2.4.13).
        const enhanced = !!this.settings?.enhancedFocusOutline;
        const outlineWidth = enhanced ? '4px' : '3px';
        const outlineOffset = enhanced ? '3px' : '2px';
        const styleTag = document.createElement('style');
        styleTag.id = 'pixelmate-a11y-css';
        styleTag.type = 'text/css';
        styleTag.textContent = `
            #pixelmate-banner :focus-visible,
            #pixelmate-revoke-button:focus-visible,
            .pixelmate-settings-modal :focus-visible {
                outline: ${outlineWidth} solid ${accent} !important;
                outline-offset: ${outlineOffset} !important;
            }
            .pixelmate-settings-modal input:focus-visible + .pixelmate-toggle-slider,
            .pixelmate-settings-modal input:focus-visible + .pixelmate-service-toggle-slider {
                outline: ${outlineWidth} solid ${accent} !important;
                outline-offset: ${outlineOffset} !important;
            }
            /* Für Screenreader angehängter Hinweis-Text (z.B. "öffnet in neuem
               Tab" an externen Links), visuell versteckt aber vorlesbar. Diese
               Regel MUSS hier (per JS injiziert) stehen statt in pixelmate.css -
               dieses Stylesheet wird im echten Frontend nicht geladen, siehe
               RecaptchaFormGuard.js/TurnstileFormGuard.js. */
            #pixelmate-banner .pixelmate-sr-only,
            .pixelmate-settings-modal .pixelmate-sr-only {
                position: absolute !important;
                width: 1px !important;
                height: 1px !important;
                padding: 0 !important;
                margin: -1px !important;
                overflow: hidden !important;
                clip: rect(0, 0, 0, 0) !important;
                white-space: nowrap !important;
                border: 0 !important;
            }
            /* Vergrößerte Klickfläche (WCAG 2.5.8, mind. 24x24px) für kleine
               Toggle-/Akkordeon-Icons, ohne deren sichtbare Größe zu ändern -
               siehe Kommentar oben, warum das hier statt in pixelmate.css steht. */
            #pixelmate-banner .pixelmate-toggle-clickable,
            .pixelmate-settings-modal .pixelmate-category-expand-icon,
            .pixelmate-settings-modal .pixelmate-service-expand-icon {
                position: relative;
            }
            #pixelmate-banner .pixelmate-toggle-clickable::after,
            .pixelmate-settings-modal .pixelmate-category-expand-icon::after,
            .pixelmate-settings-modal .pixelmate-service-expand-icon::after {
                content: "";
                position: absolute;
                top: 50%;
                left: 50%;
                width: 24px;
                height: 24px;
                min-width: 100%;
                min-height: 100%;
                transform: translate(-50%, -50%);
            }
        `;
        document.head.appendChild(styleTag);
    }

    createOverlay() {
        this.overlayElement = document.createElement('div');
        this.overlayElement.id = 'pixelmate-overlay';
        this.overlayElement.className = 'pixelmate-overlay';

        const overlayStyles = {
            position: 'fixed !important',
            top: '0 !important',
            left: '0 !important',
            width: '100% !important',
            height: '100% !important',
            zIndex: '999998 !important',
            pointerEvents: 'none !important'
        };

        if (this.design.overlay.type === 'blur') {
            overlayStyles.backdropFilter = 'blur(3px) !important';
            overlayStyles.webkitBackdropFilter = 'blur(3px) !important';
        } else if (this.design.overlay.type === 'color') {
            overlayStyles.backgroundColor = `${this.design.overlay.color || 'rgba(0, 0, 0, 0.5)'} !important`;
        }

        const styleString = Object.entries(overlayStyles)
            .map(([key, value]) => `${this.camelToKebab(key)}: ${value}`)
            .join('; ');

        this.overlayElement.style.cssText = styleString;
        document.body.appendChild(this.overlayElement);
    }

    applyBannerStyles() {
        const bannerStyles = {
            position: 'fixed !important',
            zIndex: '999999 !important',
            boxSizing: 'border-box !important',
            // Flex-Column, damit max-height (siehe unten) als echte Zwangsgrenze
            // wirkt: der einzige Kind-Container (.pixelmate-banner-inner bzw.
            // .pixelmate-banner-wrapper bei Bild oben) wird per flex:1/min-
            // height:0 auf den tatsächlich verfügbaren Platz zusammengestaucht,
            // statt bei Überlänge einfach hart (und unerreichbar) abgeschnitten
            // zu werden - genau das ließ Buttons/Footer bei einer konfigurierten
            // Max-Height wie 80vh verschwinden.
            display: 'flex !important',
            flexDirection: 'column !important',
            fontFamily: 'inherit !important',
            fontSize: `${this.getTypoFontSize('text', '14px')} !important`,
            lineHeight: `${this.getTypoLineHeight('text', '1.5')} !important`,
            color: `${this.design.colors?.text || '#333333'} !important`,
            backgroundColor: `${this.design.colors?.background || '#ffffff'} !important`,
            padding: '0 !important', // Banner Container soll kein padding haben
            margin: `${this.getMarginString()} !important`,
            borderWidth: `${this.normalizeCssLength(this.design.border?.width, '0')} !important`,
            borderStyle: `${this.design.border?.style || 'solid'} !important`,
            borderColor: `${this.design.border?.color || '#cccccc'} !important`,
            borderRadius: `${this.design.border?.radius || '8px'} !important`,
            boxShadow: `${this.getBoxShadowString()} !important`,
            opacity: '0 !important',
            transform: `${this.getInitialTransform()} !important`,
            transition: `all ${this.design.animations?.entranceDuration || 300}ms ${this.design.animations?.easing || 'ease-out'} !important`,
            width: `${this.getBannerWidth()} !important`,
            height: `${this.getBannerHeight()} !important`,
            maxWidth: `${this.getMaxWidth()} !important`,
            maxHeight: `${this.getMaxHeight()} !important`,
            overflow: 'hidden !important', // Changed from visible to hidden for smooth animations
            pointerEvents: 'auto !important',
            textAlign: 'left !important',
            direction: 'ltr !important'
        };

        // Im expandable Modus: initiale Breite = kollabierte Breite, max-height = expanded max-height
        // (nicht getMaxHeight() mit Default 200px – das würde die Expansion auf 200px clipping!)
        if (this.settings.bannerMode !== 'standard') {
            bannerStyles.width = `${this.getCollapsedWidth()} !important`;
            bannerStyles.height = 'auto !important';
            bannerStyles.maxHeight = `${this.getExpandedMaxHeight()} !important`;
        }

        // Position basierend auf Banner Style
        this.setBannerPosition(bannerStyles);

        const styleString = Object.entries(bannerStyles)
            .map(([key, value]) => `${this.camelToKebab(key)}: ${value}`)
            .join('; ');

        this.bannerElement.style.cssText = styleString;
    }

    setBannerPosition(styles) {
        const bannerStyle = this.design.bannerStyle || 'leiste_unten';
        const position = this.design.bannerPosition || 'unten_rechts';

        switch (bannerStyle) {
            case 'fullscreen':
                styles.top = '50% !important';
                styles.left = '50% !important';
                styles.margin = '0 !important';
                break;

            case 'minimal': {
                const isMobileDevice = this.getDeviceType() === 'mobile';

                if (isMobileDevice) {
                    // On mobile: full-width bottom/top bar with flush edges
                    const r = this.design.border?.radius || '8px';
                    if (position === 'oben_links' || position === 'oben_rechts') {
                        styles.top = '0 !important';
                        styles.left = '0 !important';
                        styles.right = '0 !important';
                        styles.margin = '0 !important';
                        // No top border-radius on top bar either (flush with top edge)
                        styles.borderRadius = `0 0 ${r} ${r} !important`;
                    } else {
                        // Bottom positions: flush bottom, round top corners
                        styles.bottom = '0 !important';
                        styles.left = '0 !important';
                        styles.right = '0 !important';
                        styles.margin = '0 !important';
                        styles.borderRadius = `${r} ${r} 0 0 !important`;
                    }
                } else {
                    const m = this.getSpacingConfig().margin || {};
                    switch (position) {
                        case 'unten_links':
                            styles.bottom = `${m.bottom || '20px'} !important`;
                            styles.left = `${m.left || '20px'} !important`;
                            break;
                        case 'unten_rechts':
                            styles.bottom = `${m.bottom || '20px'} !important`;
                            styles.right = `${m.right || '20px'} !important`;
                            break;
                        case 'oben_links':
                            styles.top = `${m.top || '20px'} !important`;
                            styles.left = `${m.left || '20px'} !important`;
                            break;
                        case 'oben_rechts':
                            styles.top = `${m.top || '20px'} !important`;
                            styles.right = `${m.right || '20px'} !important`;
                            break;
                    }
                }
                break;
            }

            case 'leiste_unten_bar':
                // Echte volle Leiste (siehe getBarLayoutHTML()): randlos über die
                // gesamte Breite, kein zentrierender auto-Margin nötig.
                styles.bottom = '0 !important';
                styles.left = '0 !important';
                styles.right = '0 !important';
                styles.margin = '0 !important';
                styles.borderRadius = '0 !important';
                break;

            case 'leiste_unten':
            default:
                styles.bottom = '0 !important';
                styles.left = '0 !important';
                styles.right = '0 !important';
                // margin: 0 auto zentriert die Leiste, sobald max-width (800px) greift
                styles.margin = '0 auto !important';
                styles.borderRadius = '0 !important';
                break;
        }
    }

    getDeviceType() {
        const w = window.innerWidth;
        if (w <= 768) return 'mobile';
        if (w < 1200) return 'tablet';
        return 'desktop';
    }

    /**
     * Der Hover-Expand-Modus ("Erweiterbar (Hover)") setzt einen echten
     * Mauszeiger voraus - auf Touch-Geräten (Mobil/Tablet) gibt es keinen
     * echten Hover-Zustand, nur Tippen, wodurch der erweiterte Inhalt dort
     * praktisch unerreichbar wäre. Außerhalb von Desktop fällt er deshalb
     * automatisch auf den klickbaren "Erweiterbar (Button)"-Modus zurück
     * (siehe Hinweistext unter der Auswahl in den Design-Einstellungen).
     */
    getEffectiveBannerMode() {
        if (this.settings.bannerMode === 'expandable_hover' && this.getDeviceType() !== 'desktop') {
            return 'expandable_button';
        }
        return this.settings.bannerMode;
    }

    getSizeConfig() {
        const device = this.getDeviceType();
        if (device === 'mobile') return this.design.size?.mobile || {};
        if (device === 'tablet') {
            // Tablet falls back to desktop if tablet config is missing
            const tablet = this.design.size?.tablet;
            return (tablet && Object.keys(tablet).length) ? tablet : (this.design.size?.desktop || {});
        }
        return this.design.size?.desktop || {};
    }

    getBannerWidth() {
        const bannerStyle = this.design.bannerStyle || 'leiste_unten';

        if (bannerStyle === 'leiste_unten') {
            return '100%';
        }

        const device = this.getDeviceType();
        if (device === 'mobile') return this.design.size?.mobile?.width || '100vw';
        if (device === 'tablet') return this.design.size?.tablet?.width || this.design.size?.desktop?.width || '550px';
        return this.design.size?.desktop?.width || '550px';
    }

    getCollapsedWidth() {
        const bannerStyle = this.design.bannerStyle || 'leiste_unten';

        if (bannerStyle === 'leiste_unten') {
            return '100%';
        }

        const sizeConf = this.getSizeConfig();
        const fallbackWidth = this.getBannerWidth();
        const collapsedWidth = sizeConf.collapsedWidth;
        return (collapsedWidth && collapsedWidth.trim()) ? collapsedWidth : fallbackWidth;
    }

    getBannerHeight() {
        const bannerStyle = this.design.bannerStyle || 'leiste_unten';

        if (bannerStyle === 'leiste_unten') {
            return 'auto';
        }

        return this.getSizeConfig().height || 'auto';
    }

// Updated getBannerHTML mit Padding auf pixelmate-banner-inner

    getBannerHTML() {
        // "Leiste unten" (echte volle Leiste) hat ein eigenes 3-Spalten-Layout
        // (Text / Kategorien / Buttons+Footer, siehe getBarLayoutHTML()) statt des
        // einspaltigen Scroll-Bereichs der übrigen Banner-Stile.
        if (this.design.bannerStyle === 'leiste_unten_bar') {
            return this.getBarLayoutHTML();
        }

        const imageData = this.getBannerImageHTML();

        // "Mehr"-Link wird Teil des scrollbaren Contents, direkt über den Buttons -
        // wie Buttons und Footer bleibt er dadurch auch im kollabierten Zustand
        // erreichbar, ohne eine eigene Sonderbehandlung außerhalb des Containers
        // zu benötigen.
        const isExpandableButtonMode = this.getEffectiveBannerMode() === 'expandable_button';
        const moreButtonHTML = isExpandableButtonMode ? this.getMoreButtonHTML() : '';

        // Titel, Text, erweiterter Text, Dienste-Details und Kategorien scrollen
        // gemeinsam in diesem Bereich, falls sie die verfügbare Höhe überschreiten.
        // Buttons und Footer stehen bewusst AUSSERHALB davon (siehe unten) und
        // bleiben dadurch immer vollständig sichtbar - unabhängig vom Banner-Modus.
        const scrollAreaStyle = 'flex: 1 1 auto !important; min-height: 0 !important; overflow-y: auto !important; overflow-x: hidden !important; -webkit-overflow-scrolling: touch !important;';
        const contentHTML = `<div class="pixelmate-banner-scroll-area" style="${scrollAreaStyle}">${this.getBannerContentHTML()}${moreButtonHTML}</div>`;
        const buttonsHTML = this.getButtonsHTML();
        const footerHTML = this.getFixedFooterHTML();

        // Inner padding styles + collapsed height für expandable Modi
        const innerPaddingStyle = `padding: ${this.getPaddingString()} !important;`;
        const innerHeightStyle = this.getInnerHeightStyle();
        const contentMaxWidthStyle = `max-width: ${this.getBannerContentMaxWidth()} !important; box-sizing: border-box !important;`;

        // Layout mit Bild außerhalb des Hauptcontainers
        if (imageData && imageData.position && imageData.position === 'top') {
            return `
        <div class="pixelmate-banner-wrapper" style="display: flex !important; flex-direction: column !important; flex: 1 !important; min-height: 0 !important; width: 100% !important; height: 100% !important;">
            ${imageData.html}
            <div class="pixelmate-banner-inner" style="${innerPaddingStyle} ${innerHeightStyle} display: flex !important; flex-direction: column !important; flex: 1 !important; min-height: 0 !important; width: 100% !important; box-sizing: border-box !important; transition: max-height 0.6s cubic-bezier(0.22, 1, 0.36, 1) !important; ${this.getInnerOverflowStyle()}">
                <div class="pixelmate-content-area" style="display: flex !important; align-items: stretch !important; gap: 15px !important; width: 100% !important; flex: 1 !important; min-height: 0 !important; min-width: 0 !important;">
                    <div class="pixelmate-banner-content" style="display: flex !important; flex-direction: column !important; flex: 1 !important; min-height: 0 !important; min-width: 0 !important; ${contentMaxWidthStyle}">
                        ${contentHTML}
                        ${buttonsHTML}
                    </div>
                </div>
                ${footerHTML}
            </div>
        </div>
        `;
        }

        // Standard Layout mit Bild innerhalb der Content-Area
        if (imageData && imageData.position) {
            return this.getLayoutWithImageAndFooter(imageData, contentHTML, buttonsHTML, footerHTML, innerPaddingStyle, innerHeightStyle);
        }

        // Standard Layout ohne Bild aber mit festem Footer
        return `
    <div class="pixelmate-banner-inner" style="${innerPaddingStyle} ${innerHeightStyle} display: flex !important; flex-direction: column !important; flex: 1 !important; min-height: 0 !important; width: 100% !important; box-sizing: border-box !important; transition: max-height 0.6s cubic-bezier(0.22, 1, 0.36, 1) !important; ${this.getInnerOverflowStyle()}">
        <div class="pixelmate-content-area" style="display: flex !important; align-items: stretch !important; gap: 15px !important; width: 100% !important; flex: 1 !important; min-height: 0 !important; min-width: 0 !important;">
            <div class="pixelmate-banner-content" style="display: flex !important; flex-direction: column !important; flex: 1 !important; min-height: 0 !important; min-width: 0 !important; ${contentMaxWidthStyle}">
                ${contentHTML}
                ${buttonsHTML}
            </div>
        </div>
        ${footerHTML}
    </div>
    `;
    }

    /**
     * Layout für den Banner-Style "Leiste unten" (echte volle Leiste, siehe
     * Design-Einstellungen): eine über die gesamte Breite laufende, unten
     * fixierte Leiste mit drei nebeneinander liegenden Spalten - links der
     * Cookie-Text, mittig die Kategorien (nur wenn die Kategorie-Checkboxen
     * aktiviert sind), rechts Buttons + Footer (Links/Sprachumschalter). Sind
     * keine Kategorien sichtbar, werden es zwei Spalten. Auf schmalen
     * Bildschirmen greift die generische Mobil-CSS-Regel
     * (.pixelmate-bar-columns, siehe pixelmate.css) und stapelt die Spalten
     * untereinander.
     */
    getBarLayoutHTML() {
        const titleHTML = this.getTitleHTML();
        const textHTML = this.getTextHTML();
        const serviceDetailsHTML = this.getServiceDetailsHTML();
        const isExpandableButtonMode = this.getEffectiveBannerMode() === 'expandable_button';
        const moreButtonHTML = isExpandableButtonMode ? this.getMoreButtonHTML() : '';
        const categoryCheckboxesHTML = this.getCategoryCheckboxesHTML();
        const hasCategoriesColumn = !!this.settings.showCategoryCheckboxes;
        const buttonsHTML = this.getButtonsHTML();
        const footerHTML = this.getFixedFooterHTML();

        const innerPaddingStyle = `padding: ${this.getPaddingString()} !important;`;
        const innerHeightStyle = this.getInnerHeightStyle();

        // Jede Spalte scrollt bei Bedarf für sich (overflow-y/min-height:0),
        // damit z.B. viele Kategorien oder ein langer Cookie-Text nicht die
        // Buttons-Spalte aus dem sichtbaren Bereich drängen.
        const columnStyle = 'flex: 1 1 0 !important; min-width: 0 !important; min-height: 0 !important; overflow-y: auto !important; overflow-x: hidden !important;';

        const textColumnHTML = `
        <div class="pixelmate-bar-col pixelmate-bar-col-text" style="${columnStyle}">
            ${titleHTML}
            ${textHTML}
            ${serviceDetailsHTML}
            ${moreButtonHTML}
        </div>`;

        const categoriesColumnHTML = hasCategoriesColumn ? `
        <div class="pixelmate-bar-col pixelmate-bar-col-categories" style="${columnStyle}">
            ${categoryCheckboxesHTML}
        </div>` : '';

        // Buttons/Footer bleiben - wie in den übrigen Banner-Stilen - immer
        // vollständig sichtbar (flex-shrink:0 auf .pixelmate-buttons/.pixelmate-footer),
        // die Spalte selbst scrollt daher nicht. Mit justify-content: flex-end werden
        // Buttons und Footer nach unten gepusht (zum Ende der Spalte).
        const actionsColumnHTML = `
        <div class="pixelmate-bar-col pixelmate-bar-col-actions" style="flex: 1 1 0 !important; min-width: 0 !important; min-height: 0 !important; display: flex !important; flex-direction: column !important; justify-content: flex-end !important;">
            ${buttonsHTML}
            ${footerHTML}
        </div>`;

        return `
    <div class="pixelmate-banner-inner" style="${innerPaddingStyle} ${innerHeightStyle} display: flex !important; flex-direction: column !important; flex: 1 !important; min-height: 0 !important; width: 100% !important; box-sizing: border-box !important; transition: max-height 0.6s cubic-bezier(0.22, 1, 0.36, 1) !important; ${this.getInnerOverflowStyle()}">
        <div class="pixelmate-bar-columns" style="display: flex !important; flex-direction: row !important; align-items: stretch !important; gap: 24px !important; width: 100% !important; flex: 1 !important; min-height: 0 !important;">
            ${textColumnHTML}
            ${categoriesColumnHTML}
            ${actionsColumnHTML}
        </div>
    </div>
    `;
    }

    getInnerHeightStyle() {
        const isExpandable = this.settings.bannerMode !== 'standard';

        if (!isExpandable) {
            // Standard-Modus: Der innere Container wird auf EXAKT dieselbe Höhe
            // gedeckelt wie der äußere Banner (getMaxHeight(), respektiert eine
            // konfigurierte Design-Max-Height wie z.B. 80vh). Beide MÜSSEN
            // übereinstimmen: Der äußere Banner clippt mit overflow:hidden hart
            // (kein Scrollen) - wäre der innere Container höher als der äußere
            // erlaubt, würde alles unterhalb der äußeren Grenze unsichtbar UND
            // unerreichbar, inklusive Buttons und Footer. Buttons und Footer sind
            // innen flex-shrink:0 (siehe getButtonsHTML()/getFixedFooterHTML())
            // und bleiben deshalb immer vollständig sichtbar; nur der Scroll-
            // Bereich (.pixelmate-banner-scroll-area, siehe getBannerHTML()) wird
            // bei Bedarf intern scrollbar.
            return `height: auto !important; max-height: ${this.getMaxHeight()} !important;`;
        }

        // Bei expandable Modi: max-height auf none setzen, damit der sichtbare
        // Inhalt (Titel + Text + Padding) nie abgeschnitten wird. Der SmoothExpander
        // setzt vor der Animation den gemessenen Pixelwert als Ausgangspunkt.
        return 'max-height: none !important;';
    }

    /**
     * Overflow-Verhalten des inneren Containers - immer hidden, unabhängig vom
     * Banner-Modus. Das Scrollen bei Überlänge übernimmt stattdessen einheitlich
     * der Scroll-Bereich (.pixelmate-banner-scroll-area) darin, damit Buttons und
     * Footer als Geschwister-Elemente davon unberührt und immer sichtbar bleiben.
     * Für die expandable Modi ist overflow:hidden zusätzlich zwingend erforderlich,
     * weil der SmoothExpander die Höhe über eine max-height-Transition animiert —
     * ein scrollbarer Container würde die Animation sichtbar zerstören.
     */
    getInnerOverflowStyle() {
        return 'overflow: hidden !important;';
    }

    /**
     * Deckelt den inneren Container nach Abschluss der Expand-Animation auf
     * EXAKT dieselbe Höhe wie der äußere Banner (getExpandedMaxHeight() - die
     * respektiert eine konfigurierte Design-Max-Height wie z.B. 80vh, genau wie
     * applyBannerStyles() sie auf den äußeren Banner anwendet). Der
     * SmoothExpander animiert zur vollen, ungekürzten Content-Höhe (siehe
     * measureExpandedHeight()), die größer sein kann als die konfigurierte
     * Max-Height. Ohne diesen Schritt bliebe der innere Container höher als der
     * äußere Banner erlaubt - der äußere clippt mit overflow:hidden hart (kein
     * Scrollen), wodurch alles unterhalb seiner Grenze unsichtbar UND
     * unerreichbar würde, inklusive Buttons und Footer. Mit übereinstimmender
     * Höhe übernimmt stattdessen der Scroll-Bereich (.pixelmate-banner-scroll-
     * area) dank Flexbox automatisch das interne Scrollen, während Buttons und
     * Footer (flex-shrink:0) weiterhin fix sichtbar bleiben. Wird erst NACH der
     * Animation aufgerufen, damit die Höhenmessung des SmoothExpander unberührt
     * bleibt (dieser misst mit overflow:visible / max-height:none).
     */
    capInnerHeightToViewport(inner) {
        if (!inner) return;
        inner.style.maxHeight = this.getExpandedMaxHeight();
    }

    getFixedFooterHTML() {
        const languageSwitcherHTML = this.getLanguageSwitcherHTML();
        const linksHTML = this.getLinksHTML();

        if (!languageSwitcherHTML && !linksHTML) {
            return '';
        }

        // Der Footer steht außerhalb des Scroll-Bereichs (siehe getBannerHTML())
        // und ist deshalb immer sichtbar - unabhängig vom Banner-Modus.
        // flex-shrink:0 verhindert, dass er im Flex-Layout gestaucht wird.
        // In getBarLayoutHTML() wird der Footer mit Hilfe eines wrapper-divs mit margin-top: auto
        // nach unten gepusht - aber um das zu unterstützen und nicht zu dublizieren,
        // kann der wrapper DIE margin-top:15px haben statt des Footers selbst.
        // Um Kompatibilität zu wahren, bleibt die margin-top:15px aber hier.
        return `
    <div class="pixelmate-footer" style="
        display: flex !important;
        flex-shrink: 0 !important;
        justify-content: space-between !important;
        align-items: center !important;
        margin-top: 15px !important;
        padding-top: 10px !important;
        border-top: 1px solid rgba(255,255,255,0.3) !important;
        min-height: 24px !important;
    ">
        ${languageSwitcherHTML}
        ${linksHTML}
    </div>
    `;
    }

    getLanguageSwitcherHTML() {
        const additionalLanguages = this.config.settings?.additionalLanguages || [];
        const originalMainLanguage = this.config.settings?.language || 'de_DE';
        const currentLanguage = this.settings.language || originalMainLanguage;

        const availableLanguages = [];
        const processedLanguages = new Set();

        availableLanguages.push({
            code: originalMainLanguage,
            label: this.getLanguageLabel(originalMainLanguage),
            isDefault: true
        });
        processedLanguages.add(originalMainLanguage);

        // Mitgelieferte Hauptsprachen (DE/EN/FR/ES/IT/NL/PL/DA) sind immer
        // wählbar, unabhängig davon, ob der Admin sie explizit als Zusatzsprache
        // angelegt hat. Dedupe über den zweistelligen Sprachpräfix, damit z.B. bei
        // Hauptsprache "de_AT" nicht zusätzlich ein zweiter "DE"-Eintrag
        // ("de_DE") auftaucht.
        Object.entries(PixelmateBanner.CORE_LANGUAGE_LOCALES).forEach(([prefix, code]) => {
            const alreadyCovered = [...processedLanguages].some(existing => existing.toLowerCase().startsWith(prefix));
            if (!alreadyCovered) {
                availableLanguages.push({
                    code,
                    label: this.getLanguageLabel(code),
                    isDefault: false
                });
                processedLanguages.add(code);
            }
        });

        // Benutzerdefiniert angelegte Zusatzsprachen ergänzen die Hauptsprachen.
        additionalLanguages.forEach(lang => {
            if (lang.language_code && !processedLanguages.has(lang.language_code)) {
                availableLanguages.push({
                    code: lang.language_code,
                    label: this.getLanguageLabel(lang.language_code),
                    isDefault: false
                });
                processedLanguages.add(lang.language_code);
            }
        });

        if (availableLanguages.length < 2) {
            return '';
        }

        const textColor = this.design.colors?.text || '#333333';

        const optionsHTML = availableLanguages
            .map(lang => {
                const isActive = lang.code === currentLanguage;
                return `
                    <button type="button" class="pixelmate-lang-btn" data-lang="${lang.code}" role="menuitemradio" aria-checked="${isActive}" style="
                        display: block !important;
                        width: 100% !important;
                        box-sizing: border-box !important;
                        text-align: left !important;
                        background: ${isActive ? 'rgba(128,128,128,0.15)' : 'none'} !important;
                        border: none !important;
                        padding: 6px 14px !important;
                        font-size: 12px !important;
                        font-weight: ${isActive ? '700' : '400'} !important;
                        color: ${textColor} !important;
                        cursor: pointer !important;
                        letter-spacing: 0.5px !important;
                        text-transform: uppercase !important;
                        white-space: nowrap !important;
                        font-family: inherit !important;
                    " onmouseover="this.style.background='rgba(128,128,128,0.15)'" onmouseout="this.style.background='${isActive ? 'rgba(128,128,128,0.15)' : 'none'}'">
                        ${lang.label}
                    </button>
                `;
            })
            .join('');

        // Kompakter Umschalter: Globus-Icon + "Lang" öffnet per Klick ein
        // Dropdown mit den einzelnen Sprachoptionen (siehe attachLanguageSwitcherEvents()),
        // statt dauerhaft alle Sprachkürzel im knappen Footer aufzulisten.
        return `
        <div class="pixelmate-language-switcher" style="position: relative !important; display: inline-flex !important; align-items: center !important;">
            <button type="button" class="pixelmate-lang-toggle" aria-haspopup="true" aria-expanded="false" style="
                display: inline-flex !important;
                align-items: center !important;
                gap: 4px !important;
                background: none !important;
                border: none !important;
                padding: 2px 4px !important;
                font-size: 11px !important;
                font-weight: 400 !important;
                color: ${textColor} !important;
                opacity: 0.75 !important;
                cursor: pointer !important;
                letter-spacing: 0.3px !important;
                text-transform: uppercase !important;
                line-height: 1 !important;
                font-family: inherit !important;
            ">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0 !important;" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                </svg>
                <span>Lang</span>
            </button>
            <div class="pixelmate-lang-menu" role="menu" style="
                display: none !important;
                position: absolute !important;
                bottom: 100% !important;
                left: 0 !important;
                margin-bottom: 6px !important;
                background: #ffffff !important;
                border: 1px solid rgba(0,0,0,0.1) !important;
                border-radius: 6px !important;
                box-shadow: 0 2px 12px rgba(0,0,0,0.18) !important;
                min-width: 88px !important;
                overflow: hidden !important;
                z-index: 10 !important;
            ">
                ${optionsHTML}
            </div>
        </div>
        `;
    }

    getLanguageLabel(languageCode) {
        const labels = {
            'de_DE': 'DE',
            'en_US': 'EN',
            'en_GB': 'EN',
            'fr_FR': 'FR',
            'es_ES': 'ES',
            'it_IT': 'IT',
            'nl_NL': 'NL',
            'pt_PT': 'PT',
            'ru_RU': 'RU',
            'pl_PL': 'PL',
            'sv_SE': 'SV',
            'da_DK': 'DK',
            'no_NO': 'NO',
            'fi_FI': 'FI',
            'cs_CZ': 'CZ',
            'hu_HU': 'HU',
            'tr_TR': 'TR'
        };

        return labels[languageCode] || languageCode.substring(0, 2).toUpperCase();
    }

    getLayoutWithImageAndFooter(imageData, contentHTML, buttonsHTML, footerHTML, innerPaddingStyle, innerHeightStyle) {
        const { html: imageHTML, position } = imageData;

        const baseLayout = `
    <div class="pixelmate-banner-inner" style="${innerPaddingStyle} ${innerHeightStyle} display: flex !important; flex-direction: column !important; flex: 1 !important; min-height: 0 !important; width: 100% !important; box-sizing: border-box !important; transition: max-height 0.6s cubic-bezier(0.22, 1, 0.36, 1) !important; ${this.getInnerOverflowStyle()}">
        <div class="pixelmate-content-area" style="display: flex !important; gap: 15px !important; width: 100% !important; flex: 1 !important; min-height: 0 !important; min-width: 0 !important;">
            ${this.getImageContentLayout(imageHTML, contentHTML, buttonsHTML, position)}
        </div>
        ${footerHTML}
    </div>
    `;

        return baseLayout;
    }

    getBannerImageHTML() {
        if (!this.design.image?.src) {
            return '';
        }

        const isExpandable = this.settings.bannerMode !== 'standard';
        const hideImage = isExpandable && this.settings.hideImageUntilExpanded;

        const imageStyles = {
            width: hideImage ? '0' : (this.design.image.width || 'auto'),
            height: hideImage ? '0' : (this.design.image.height || '80px'),
            borderRadius: this.normalizeCssLength(this.design.image.borderRadius, '0'),
            border: this.getImageBorderString(),
            objectFit: 'cover',
            flexShrink: '0',
            opacity: hideImage ? '0' : '1',
            transition: 'opacity 0.4s ease-out, width 0.4s ease-out, height 0.4s ease-out',
            overflow: 'hidden'
        };

        // Wenn Position "top" ist, Bild ohne Padding-Beeinflussung
        if (this.design.image.position === 'top') {
            imageStyles.marginBottom = '15px';
            imageStyles.alignSelf = 'center';
            imageStyles.position = 'relative';
            imageStyles.zIndex = '1';
        }

        const styleString = Object.entries(imageStyles)
            .map(([key, value]) => `${this.camelToKebab(key)}: ${value}`)
            .join('; ');

        const position = this.design.image.position || 'top';
        const imageHTML = `<img src="${this.design.image.src}" alt="Banner" class="pixelmate-banner-image" style="${styleString}" />`;

        return { html: imageHTML, position: position };
    }

    getBannerContentHTML() {
        const titleHTML = this.getTitleHTML();
        const textHTML = this.getTextHTML();
        const serviceDetailsHTML = this.getServiceDetailsHTML();
        const categoryCheckboxesHTML = this.getCategoryCheckboxesHTML();

        return `
            ${titleHTML}
            ${textHTML}
            ${serviceDetailsHTML}
            ${categoryCheckboxesHTML}
        `;
    }

    /**
     * "Mehr Informationen zu den eingesetzten Diensten" - aufklappbarer Link unter
     * dem Banner-Text, der automatisch alle aktuell aktivierten Dienste mit Zweck
     * und Kategorie auflistet. Nutzt dieselbe Kategorisierung wie das Cookie-
     * Einstellungen-Modal (this.settingsModal.categories, siehe SettingsModal.
     * loadServices()), damit hier garantiert dieselben Dienste erscheinen, die auch
     * dort und im ServiceInjector als aktiv gelten - keine zweite, potenziell
     * abweichende Quelle.
     */
    getServiceDetailsHTML() {
        const categories = this.settingsModal?.categories;
        if (!categories) return '';

        const showCategories = this.settings.showCategories || {
            necessary: true, analytics: true, marketing: true, external: true
        };

        const bannerLang = (() => { const raw = (this.settings.language || ''); return raw.startsWith('de') ? 'de' : raw.startsWith('fr') ? 'fr' : raw.startsWith('es') ? 'es' : raw.startsWith('it') ? 'it' : raw.startsWith('nl') ? 'nl' : raw.startsWith('pl') ? 'pl' : raw.startsWith('da') ? 'da' : raw.startsWith('sv') ? 'sv' : 'en'; })();
        const categoryTexts = this.texts.categories || {};
        const escape = (str) => this.settingsModal.escapeHtml(str || '');

        const categoryOrder = ['necessary', 'analytics', 'marketing', 'external'];
        const sectionsHTML = categoryOrder
            .filter(key => showCategories[key] && categories[key]?.services?.length)
            .map(key => {
                const catName = SettingsModal.MODAL_TEXTS?.categories?.[key]?.name?.[bannerLang]
                    || categoryTexts[key]?.name
                    || key;

                const servicesHTML = categories[key].services.map(svc => {
                    const name = escape(svc.name);
                    const description = svc.description ? escape(svc.description) : '';
                    return `<li style="margin: 0 0 6px 0 !important;"><strong>${name}</strong>${description ? `: ${description}` : ''}</li>`;
                }).join('');

                return `
                <div class="pixelmate-service-category" style="margin-bottom: 12px !important;">
                    <div style="font-weight: 600 !important; margin-bottom: 4px !important;">${escape(catName)}</div>
                    <ul style="margin: 0 !important; padding-left: 18px !important; list-style: disc !important;">${servicesHTML}</ul>
                </div>`;
            }).join('');

        // Ohne aktive, sichtbare Dienste gibt es nichts aufzuklappen.
        if (!sectionsHTML) return '';

        const textColor = this.design.colors?.text || '#333333';
        const fontSize = this.getTypoFontSize('text', '14px');

        const panelStyle = `
            display: none !important;
            margin-bottom: 15px !important;
            font-size: ${fontSize} !important;
            color: ${textColor} !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
        `;

        // In den erweiterbaren Banner-Modi (Button/Hover) gibt es keinen eigenen
        // Klick-Toggle mehr für die Dienste-Details - sie werden stattdessen Teil
        // des erweiterbaren Contents (wie der erweiterte Text) und erscheinen
        // automatisch beim Erweitern/Hover des Banners, siehe
        // getExpandableContentElements(). Im Standard-Modus (kein Expand/Collapse)
        // bleibt der bisherige Klick-Toggle bestehen.
        if (this.settings.bannerMode !== 'standard') {
            return `<div class="pixelmate-service-details-panel" style="${panelStyle}">${sectionsHTML}</div>`;
        }

        const linkColor = this.design.colors?.link || '#0073aa';
        const openLabel = this.texts.serviceDetailsLink || 'Mehr Informationen zu den eingesetzten Diensten';

        return `
        <button type="button" class="pixelmate-service-details-toggle" aria-expanded="false" style="
            background: none !important;
            border: none !important;
            color: ${linkColor} !important;
            cursor: pointer !important;
            text-decoration: underline !important;
            font-size: inherit !important;
            font-family: inherit !important;
            padding: 0 !important;
            margin: 0 0 15px 0 !important;
            display: inline-block !important;
            -webkit-appearance: none !important;
            appearance: none !important;
        ">${openLabel}</button>
        <div class="pixelmate-service-details-panel" style="${panelStyle}">${sectionsHTML}</div>`;
    }

    toggleServiceDetails(toggleEl) {
        const panel = this.bannerElement.querySelector('.pixelmate-service-details-panel');
        if (!panel) return;

        const isOpen = toggleEl.getAttribute('aria-expanded') === 'true';
        panel.style.display = isOpen ? 'none' : 'block';
        toggleEl.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        toggleEl.textContent = isOpen
            ? (this.texts.serviceDetailsLink || 'Mehr Informationen zu den eingesetzten Diensten')
            : (this.texts.serviceDetailsLessLink || 'Details ausblenden');
    }

    getCategoryCheckboxesHTML() {
        if (!this.settings.showCategoryCheckboxes) return '';

        const showCategories = this.settings.showCategories || {
            necessary: true, analytics: true, marketing: true, external: true
        };

        const categoryTexts = this.texts.categories || {};
        const textColor = this.design.colors?.text || '#333333';
        const accentColor = this.design.buttons?.accept?.background || '#0073aa';
        const fontSize = this.getTypoFontSize('text', '14px');

        const isExpandable = this.settings.bannerMode !== 'standard';
        const hideCats = isExpandable && this.settings.hideCategoriesUntilExpanded;
        const initialDisplay = hideCats ? 'none' : 'flex';

        const categoryDefs = [
            { key: 'necessary', required: true },
            { key: 'analytics', required: false },
            { key: 'marketing', required: false },
            { key: 'external', required: false }
        ];

        const visibleCategories = categoryDefs.filter(cat => showCategories[cat.key]);

        // Sprache für zweisprachige Kategorienamen bestimmen
        const bannerLang = (() => { const raw = (this.settings.language || ''); return raw.startsWith('de') ? 'de' : raw.startsWith('fr') ? 'fr' : raw.startsWith('es') ? 'es' : raw.startsWith('it') ? 'it' : raw.startsWith('nl') ? 'nl' : raw.startsWith('pl') ? 'pl' : raw.startsWith('da') ? 'da' : raw.startsWith('sv') ? 'sv' : 'en'; })();

        const rowsHTML = visibleCategories.map((cat, index) => {
            // Zweisprachige Namen aus SettingsModal.MODAL_TEXTS (de/en), Fallback auf WP-Locale-Text
            const name = SettingsModal.MODAL_TEXTS?.categories?.[cat.key]?.name?.[bannerLang]
                || categoryTexts[cat.key]?.name
                || cat.key;
            const services = this.getServicesForCategory(cat.key);
            const isLast = index === visibleCategories.length - 1;
            const isChecked = cat.required;
            const isDisabled = cat.required;

            const thumbLeft = isChecked ? '18px' : '3px';
            const trackBg = isChecked
                ? (isDisabled ? 'rgba(0,0,0,0.15)' : accentColor)
                : 'rgba(0,0,0,0.15)';

            return `
            <div class="pixelmate-category-row" style="
                display: flex !important;
                align-items: center !important;
                justify-content: space-between !important;
                gap: 12px !important;
                padding: 10px 0 !important;
                ${!isLast ? `border-bottom: 1px solid rgba(${this.hexToRgb(textColor)}, 0.1) !important;` : ''}
                box-sizing: border-box !important;
            ">
                <div style="flex: 1 !important; min-width: 0 !important;">
                    <div class="pixelmate-category-name" data-category="${cat.key}" style="
                        font-size: ${fontSize} !important;
                        font-weight: 500 !important;
                        color: ${textColor} !important;
                        font-family: inherit !important;
                        ${isDisabled ? 'opacity: 0.6 !important;' : ''}
                    ">${name}</div>
                    ${services ? `<div style="
                        font-size: 12px !important;
                        color: ${textColor} !important;
                        opacity: 0.55 !important;
                        margin-top: 2px !important;
                        font-family: inherit !important;
                        white-space: nowrap !important;
                        overflow: hidden !important;
                        text-overflow: ellipsis !important;
                    ">${services}</div>` : ''}
                </div>
                <div
                    class="pixelmate-toggle${isDisabled ? '' : ' pixelmate-toggle-clickable'}"
                    data-category="${cat.key}"
                    data-checked="${isChecked}"
                    role="switch"
                    aria-checked="${isChecked}"
                    aria-label="${name}"
                    ${isDisabled ? 'aria-disabled="true"' : 'tabindex="0"'}
                    style="
                        position: relative !important;
                        width: 36px !important;
                        height: 20px !important;
                        border-radius: 10px !important;
                        background-color: ${trackBg} !important;
                        flex-shrink: 0 !important;
                        cursor: ${isDisabled ? 'default' : 'pointer'} !important;
                        transition: background-color 0.2s ease !important;
                        box-sizing: border-box !important;
                        ${isDisabled ? 'opacity: 0.5 !important;' : ''}
                    "
                >
                    <div class="pixelmate-toggle-thumb" style="
                        position: absolute !important;
                        top: 3px !important;
                        left: ${thumbLeft} !important;
                        width: 14px !important;
                        height: 14px !important;
                        border-radius: 50% !important;
                        background-color: #ffffff !important;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.3) !important;
                        transition: left 0.2s ease !important;
                        box-sizing: border-box !important;
                    "></div>
                </div>
                <input
                    type="checkbox"
                    class="pixelmate-category-checkbox"
                    data-category="${cat.key}"
                    ${isChecked ? 'checked' : ''}
                    ${isDisabled ? 'disabled' : ''}
                    style="display: none !important;"
                />
            </div>`;
        }).join('');

        return `
        <div class="pixelmate-category-checkboxes" style="
            display: ${initialDisplay} !important;
            flex-direction: column !important;
            margin-bottom: 15px !important;
            padding: 4px 0 !important;
            box-sizing: border-box !important;
            transition: opacity 0.4s cubic-bezier(0.22, 1, 0.36, 1) !important;
        ">
            ${rowsHTML}
        </div>`;
    }

    hexToRgb(hex) {
        if (!hex || typeof hex !== 'string') return '0,0,0';
        hex = hex.replace('#', '');
        if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
        const num = parseInt(hex, 16);
        if (isNaN(num)) return '0,0,0';
        return `${(num >> 16) & 255},${(num >> 8) & 255},${num & 255}`;
    }

    getServicesForCategory(category) {
        // Dieselbe, vollständige Kategorisierung wie im Einstellungen-Modal nutzen,
        // damit der Banner-Zusammenfassungstext ALLE im Integrationsmanager
        // aktivierten Dienste der Kategorie zeigt (inkl. Script-erkannter und
        // benutzerdefinierter Dienste) statt einer veralteten, hartkodierten Liste.
        const modalCategory = this.settingsModal?.categories?.[category];
        if (!modalCategory || !Array.isArray(modalCategory.services)) {
            return '';
        }

        return modalCategory.services
            .map(svc => svc.name)
            .filter(Boolean)
            .join(', ');
    }

    getTitleHTML() {
        if (!this.texts.title) return '';

        const titleStyles = {
            fontSize: `${this.getTypoFontSize('title', '18px')} !important`,
            fontWeight: `${this.design.typography?.title?.fontWeight || '600'} !important`,
            fontStyle: `${this.design.typography?.title?.fontStyle || 'normal'} !important`,
            lineHeight: `${this.getTypoLineHeight('title', '1.4')} !important`,
            margin: '0 0 10px 0 !important',
            padding: '0 !important',
            color: 'inherit !important',
            fontFamily: 'inherit !important',
            border: 'none !important',
            background: 'none !important',
            textTransform: 'none !important',
            letterSpacing: 'normal !important'
        };

        const styleString = Object.entries(titleStyles)
            .map(([key, value]) => `${this.camelToKebab(key)}: ${value}`)
            .join('; ');

        return `<h3 id="pixelmate-banner-title" class="pixelmate-title" style="${styleString}">${this.texts.title}</h3>`;
    }

    getTextHTML() {
        const mainText = this.texts.text || '';
        const extendedText = this.texts.extendedText || '';
        const showExtended = this.settings.bannerMode !== 'standard';

        // Das Scrollen bei Überlänge übernimmt jetzt einheitlich der umgebende
        // Scroll-Bereich (.pixelmate-banner-scroll-area, siehe getBannerHTML()) für
        // Titel + Text + erweiterten Text + Kategorien zusammen - kein eigenes
        // max-height/overflow hier nötig, unabhängig vom Banner-Modus.
        const wrapStyle = 'overflow-wrap: anywhere !important; word-break: break-word !important; white-space: pre-line !important;';

        let html = `<div class="pixelmate-text" style="${wrapStyle} margin-bottom: 15px !important; padding: 0 !important;">${mainText}</div>`;

        // Kurze, fett hervorgehobene Info zu den tatsächlich aktiven Kategorien -
        // immer sichtbar, auch im kollabierten Zustand erweiterbarer Banner-Modi
        // (anders als der erweiterte Text/die Kategorie-Checkboxen darunter).
        html += this.getCategoryInfoHTML();

        if (showExtended && extendedText) {
            const isExpandable = this.settings.bannerMode === 'expandable_button';
            const isHover = this.settings.bannerMode === 'expandable_hover';
            const isHidden = isExpandable || isHover;

            // display:none statt visibility:hidden+max-height:0, damit auch Margins
            // (margin-bottom:15px) nicht als leerer Platz im kollabierten Banner erscheinen
            const hiddenStyles = isHidden
                ? 'display: none !important;'
                : '';

            html += `<div class="pixelmate-extended-text" style="${hiddenStyles} ${wrapStyle} margin-bottom: 15px !important; padding: 0 !important; transition: opacity 0.4s cubic-bezier(0.22, 1, 0.36, 1) !important;">${extendedText}</div>`;
        }

        return html;
    }

    /**
     * Baut den Text "Kategorie: Notwendig, Statistik, ..." aus den Kategorien,
     * die laut showCategories tatsächlich sichtbar sind (siehe
     * build_visible_categories() in SettingsLocalizer.php - eine Kategorie
     * erscheint dort nur, wenn mindestens ein zugehöriger Dienst aktiv ist).
     * Wird sowohl beim initialen Rendern als auch beim Sprachwechsel genutzt.
     */
    buildCategoryInfoText(textsObj, languageCode) {
        const showCategories = this.settings.showCategories || {
            necessary: true, analytics: true, marketing: true, external: true
        };
        const categoryTexts = textsObj?.categories || {};
        const raw = (languageCode || '');
        const lang = raw.startsWith('de') ? 'de' : raw.startsWith('fr') ? 'fr' : raw.startsWith('es') ? 'es' : raw.startsWith('it') ? 'it' : raw.startsWith('nl') ? 'nl' : raw.startsWith('pl') ? 'pl' : raw.startsWith('da') ? 'da' : raw.startsWith('sv') ? 'sv' : 'en';

        const names = ['necessary', 'analytics', 'marketing', 'external']
            .filter(key => showCategories[key])
            .map(key => categoryTexts[key]?.name || SettingsModal.MODAL_TEXTS?.categories?.[key]?.name?.[lang] || key);

        if (!names.length) return '';

        const label = SettingsModal.MODAL_TEXTS?.categoryInfoLabel?.[lang] || 'Category:';
        return `${label} ${names.join(', ')}.`;
    }

    getCategoryInfoHTML() {
        if (this.settings.showCategoryInfoText === false) return '';

        const text = this.buildCategoryInfoText(this.texts, this.settings.language);
        if (!text) return '';

        return `<div class="pixelmate-category-info" style="font-weight: 700 !important; margin-bottom: 15px !important; overflow-wrap: anywhere !important; word-break: break-word !important;">${text}</div>`;
    }

    getMoreButtonHTML() {
        // Erscheint jetzt als Teil des normalen Contents direkt über den Buttons
        // (siehe getBannerHTML()) - übernimmt daher die horizontale Ausrichtung
        // des umgebenden Contents automatisch und braucht keine eigene
        // horizontale Polsterung. Vertikale Polsterung + Abstand nach unten
        // sorgen für eine ausreichend große Klickfläche und Trennung von den
        // Buttons darunter, statt eines eng am Text anliegenden Links.
        const moreButtonStyles = {
            background: 'none !important',
            border: 'none !important',
            color: `${this.design.colors?.link || '#0073aa'} !important`,
            cursor: 'pointer !important',
            textDecoration: 'underline !important',
            fontSize: 'inherit !important',
            fontFamily: 'inherit !important',
            display: 'inline-block !important',
            padding: '6px 0 !important',
            margin: '0 0 15px 0 !important',
            webkitAppearance: 'none !important',
            mozAppearance: 'none !important',
            appearance: 'none !important'
        };

        const styleString = Object.entries(moreButtonStyles)
            .map(([key, value]) => `${this.camelToKebab(key)}: ${value}`)
            .join('; ');

        return `<button class="pixelmate-more-btn" style="${styleString}">${this.texts.moreButton || 'Mehr anzeigen'}</button>`;
    }

    getLinksHTML() {
        const links = [];

        const linkColor = this.design.colors?.link || '#0073aa';

        const linkStyles = {
            fontSize: `${this.getTypoFontSize('link', '12px')} !important`,
            fontWeight: this.design.typography?.link?.fontWeight || '400',
            textDecoration: 'underline !important',
            color: `${linkColor} !important`,
            margin: '0 !important',
            padding: '0 4px !important'
        };

        const styleString = Object.entries(linkStyles)
            .map(([key, value]) => `${this.camelToKebab(key)}: ${value}`)
            .join('; ');

        if (this.urls.privacy) {
            links.push(`<span style="${styleString}"><a href="${this.urls.privacy}" target="_blank" class="pixelmate-privacy-link" style="color: ${linkColor} !important;" rel="noopener">${this.texts.privacyLink || 'Datenschutz'}<span class="pixelmate-sr-only">${this.texts.opensInNewTab || '(opens in new tab)'}</span></a></span>`);
        }

        if (this.urls.imprint) {
            links.push(`<span style="${styleString}"><a href="${this.urls.imprint}" target="_blank" class="pixelmate-imprint-link" style="color: ${linkColor} !important;" rel="noopener">${this.texts.imprintLink || 'Impressum'}<span class="pixelmate-sr-only">${this.texts.opensInNewTab || '(opens in new tab)'}</span></a></span>`);
        }

        // Der Cookie-Einstellungen-Button steht jetzt gleichwertig neben
        // "Alle akzeptieren"/"Alle ablehnen" (siehe getButtonsHTML) und nicht
        // mehr als Textlink im Footer.

        if (!links.length) {
            return '';
        }

        // Teil des immer sichtbaren Footers (siehe getFixedFooterHTML()).
        return `<div class="pixelmate-links" style="display: flex !important; align-items: center !important; flex-wrap: wrap !important;">
    ${links.join('<span style="opacity: 0.5 !important; padding: 0 2px !important;">|</span>')}
    </div>`;
    }

    getPrivacyUrl() {
        // Check if privacy page is selected
        const privacyPage = this.config.settings?.privacyPage;
        if (privacyPage && privacyPage !== '' && privacyPage !== 'external') {
            // It's a WordPress page ID, construct the URL
            return `${window.location.origin}/?p=${privacyPage}`;
        } else if (privacyPage === 'external' && this.config.settings?.privacyExternalUrl) {
            // It's an external URL
            return this.config.settings.privacyExternalUrl;
        } else if (this.urls.privacy) {
            // Fallback to direct URL if provided
            return this.urls.privacy;
        }
        return null;
    }

    getImprintUrl() {
        // Check if imprint page is selected
        const imprintPage = this.config.settings?.imprintPage;
        if (imprintPage && imprintPage !== '' && imprintPage !== 'external') {
            // It's a WordPress page ID, construct the URL
            return `${window.location.origin}/?p=${imprintPage}`;
        } else if (imprintPage === 'external' && this.config.settings?.imprintExternalUrl) {
            // It's an external URL
            return this.config.settings.imprintExternalUrl;
        } else if (this.urls.imprint) {
            // Fallback to direct URL if provided
            return this.urls.imprint;
        }
        return null;
    }

    getButtonOrder() {
        const styling = this.design.buttons?.styling || {};
        const device = this.getDeviceType();

        if (device === 'mobile') {
            return styling.orderMobile || 'column';
        }
        if (device === 'tablet') {
            return styling.orderTablet || styling.order || 'row';
        }
        return styling.order || 'row';
    }

    getButtonsHTML() {
        const buttonOrder = this.getButtonOrder();
        const buttonAlignment = this.design.buttons?.styling?.alignment || 'stretch';
        // Reihenfolge von "Alle akzeptieren"/"Alle ablehnen" ist über die
        // "Banner Texte"-Karte der Haupteinstellungen frei wählbar (Default:
        // Alle ablehnen zuerst, siehe pixelmate_button_display_order) - danach
        // folgen unverändert Auswahl speichern/Einstellungen (Issue #406).
        const rejectButtonHTML = this.getButtonHTML('reject', this.texts.rejectButton || 'Alle ablehnen');
        const acceptButtonHTML = this.getButtonHTML('accept', this.texts.acceptButton || 'Alle akzeptieren');
        const buttons = this.settings.buttonDisplayOrder === 'accept_first'
            ? [acceptButtonHTML, rejectButtonHTML]
            : [rejectButtonHTML, acceptButtonHTML];

        if (this.settings.showCategoryCheckboxes) {
            buttons.push(this.getButtonHTML('saveSelection', this.texts.saveSelectionButton || 'Auswahl speichern'));
        }

        buttons.push(this.getButtonHTML('settings', this.texts.cookieSettingsLink || 'Einstellungen'));

        const flexDirection = buttonOrder === 'column' ? 'column' : 'row';
        const gap = '8px';

        // Determine flex alignment based on buttonAlignment setting
        let alignItems, justifyContent;
        if (buttonAlignment === 'stretch') {
            alignItems = 'stretch';
            justifyContent = 'stretch';
        } else if (buttonAlignment === 'left') {
            alignItems = buttonOrder === 'column' ? 'flex-start' : 'center';
            justifyContent = 'flex-start';
        } else if (buttonAlignment === 'center') {
            alignItems = buttonOrder === 'column' ? 'center' : 'center';
            justifyContent = 'center';
        } else if (buttonAlignment === 'right') {
            alignItems = buttonOrder === 'column' ? 'flex-end' : 'center';
            justifyContent = 'flex-end';
        } else {
            alignItems = buttonOrder === 'column' ? 'stretch' : 'center';
            justifyContent = 'stretch';
        }

        // Buttons stehen außerhalb des Scroll-Bereichs (siehe getBannerHTML()) und
        // sind deshalb immer sichtbar - unabhängig vom Banner-Modus. flex-shrink:0
        // verhindert, dass sie im Flex-Layout gestaucht werden.
        return `<div class="pixelmate-buttons" style="display: flex !important; flex-shrink: 0 !important; flex-direction: ${flexDirection} !important; align-items: ${alignItems} !important; justify-content: ${justifyContent} !important; gap: ${gap} !important; flex-wrap: wrap !important; margin-top: 15px !important;">
        ${buttons.join('')}
    </div>`;
    }

    getButtonHTML(type, text) {
        const buttonText = text || type;

        if (!buttonText || typeof buttonText !== 'string') {
            pmWarn(`Invalid button text for ${type}:`, buttonText);
        }

        const buttonConfig = this.design.buttons?.[type] || {};
        const styling = this.design.buttons?.styling || {};
        const buttonOrder = this.getButtonOrder();

        const buttonStyles = {
            backgroundColor: `${buttonConfig.background || '#0073aa'} !important`,
            color: `${buttonConfig.text || '#ffffff'} !important`,
            border: `${this.normalizeCssLength(styling.borderWidth, '1px')} solid ${buttonConfig.background || '#0073aa'} !important`,
            borderRadius: `${this.normalizeCssLength(styling.borderRadius, '4px')} !important`,
            padding: `${this.getButtonPaddingString()} !important`,
            margin: `${this.getButtonMarginString()} !important`,
            fontSize: `${this.getTypoFontSize('button', '14px')} !important`,
            fontWeight: `${this.design.typography?.button?.fontWeight || '500'} !important`,
            fontStyle: `${this.design.typography?.button?.fontStyle || 'normal'} !important`,
            textTransform: `${this.design.typography?.button?.textTransform || 'none'} !important`,
            lineHeight: `${this.getTypoLineHeight('button', '1em')} !important`,
            height: 'auto !important',
            cursor: 'pointer !important',
            textDecoration: 'none !important',
            display: 'inline-block !important',
            transition: 'all 0.2s ease !important',
            fontFamily: 'inherit !important',
            webkitAppearance: 'none !important',
            mozAppearance: 'none !important',
            appearance: 'none !important',
            boxSizing: 'border-box !important',
            // Verhindert, dass Theme-weite Button-Styles (z.B. Kadence) über
            // background-image/box-shadow durchschlagen - background-color
            // allein wird davon nicht verdrängt.
            backgroundImage: 'none !important',
            boxShadow: 'none !important',
            textShadow: 'none !important'
        };

        // Button sizing based on alignment setting
        const buttonAlignment = styling.alignment || 'stretch';
        if (buttonAlignment === 'stretch') {
            if (buttonOrder === 'row') {
                buttonStyles.flex = '1 !important';
                buttonStyles.minWidth = '0 !important';
            } else {
                buttonStyles.width = '100% !important';
            }
        } else {
            // For left/center/right: buttons take their natural width
            buttonStyles.flex = '0 0 auto !important';
        }

        const styleString = Object.entries(buttonStyles)
            .map(([key, value]) => `${this.camelToKebab(key)}: ${value}`)
            .join('; ');

        const hoverAttrs = this.getButtonHoverAttrs(buttonConfig);

        return `<button class="pixelmate-btn pixelmate-btn-${type}" data-action="${type}" aria-label="${buttonText}" style="${styleString}" ${hoverAttrs}>${buttonText}</button>`;
    }

    /**
     * Erzeugt onmouseover/onmouseout Attribute für einen Button. Sind eigene
     * Hover-Farben konfiguriert, werden Hintergrund-/Textfarbe getauscht,
     * ansonsten dient eine reduzierte Deckkraft als Hover-Feedback.
     */
    getButtonHoverAttrs(buttonConfig) {
        const normalBg = buttonConfig.background || '#0073aa';
        const normalText = buttonConfig.text || '#ffffff';
        const hoverBg = buttonConfig.hoverBackground || '';
        const hoverText = buttonConfig.hoverText || '';

        if (!hoverBg && !hoverText) {
            return `onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'"`;
        }

        const overStyles = [
            `background-color: ${hoverBg || normalBg} !important`,
            `color: ${hoverText || normalText} !important`
        ].join('; ');
        const outStyles = [
            `background-color: ${normalBg} !important`,
            `color: ${normalText} !important`
        ].join('; ');

        return `onmouseover="this.style.cssText += '; ${overStyles}'" onmouseout="this.style.cssText += '; ${outStyles}'"`;
    }

    attachEventListeners() {
        // Button Events + Toggle Events
        this.bannerElement.addEventListener('click', (e) => {
            const action = e.target.dataset.action;
            if (action) {
                this.handleButtonClick(action);
            }

            // Mehr Button
            if (e.target.classList.contains('pixelmate-more-btn')) {
                this.toggleExtendedText();
            }

            // Mehr Informationen zu den eingesetzten Diensten
            const detailsToggle = e.target.closest('.pixelmate-service-details-toggle');
            if (detailsToggle) {
                this.toggleServiceDetails(detailsToggle);
            }

            // Category Toggle Switch
            const toggle = e.target.closest('.pixelmate-toggle-clickable');
            if (toggle) {
                this.handleToggleClick(toggle);
            }
        });

        // Tastaturbedienung der Kategorie-Schalter.
        // Die Schalter sind divs mit role="switch"; für sie erzeugt der Browser -
        // anders als bei <button> - kein Klick-Event aus Leertaste/Enter. Ohne diesen
        // Handler wäre die granulare Auswahl ausschließlich mit der Maus möglich.
        this.bannerElement.addEventListener('keydown', (e) => {
            if (e.key !== ' ' && e.key !== 'Spacebar' && e.key !== 'Enter') {
                return;
            }

            const toggle = e.target.closest('.pixelmate-toggle-clickable');
            if (!toggle) {
                return;
            }

            // Leertaste scrollt sonst die Seite, Enter kann ein umgebendes Formular absenden.
            e.preventDefault();
            this.handleToggleClick(toggle);
        });

        // Language Switcher: Globus-Icon("Lang") öffnet/schließt das Dropdown,
        // ein Klick auf eine Sprachoption wechselt die Sprache und schließt es.
        const langToggle = this.bannerElement.querySelector('.pixelmate-lang-toggle');
        const langMenu = this.bannerElement.querySelector('.pixelmate-lang-menu');
        if (langToggle && langMenu) {
            langToggle.addEventListener('click', (e) => {
                e.stopPropagation();
                const isOpen = langMenu.style.display === 'block';
                langMenu.style.display = isOpen ? 'none' : 'block';
                langToggle.setAttribute('aria-expanded', String(!isOpen));
            });

            document.addEventListener('click', (e) => {
                if (langMenu.style.display === 'block' && !this.bannerElement.contains(e.target)) {
                    langMenu.style.display = 'none';
                    langToggle.setAttribute('aria-expanded', 'false');
                }
            });

            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && langMenu.style.display === 'block') {
                    langMenu.style.display = 'none';
                    langToggle.setAttribute('aria-expanded', 'false');
                }
            });
        }

        const languageButtons = this.bannerElement.querySelectorAll('.pixelmate-lang-btn');
        languageButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                this.switchLanguageOptimized(e.currentTarget.dataset.lang);
                if (langMenu) {
                    langMenu.style.display = 'none';
                }
                if (langToggle) {
                    langToggle.setAttribute('aria-expanded', 'false');
                }
            });
        });

        // DOM-Referenzen für schnelle Updates cachen
        this.cacheElementReferences();

        // Verbesserte Hover Events für expandable_hover Modus - auf Mobil/Tablet
        // fällt der Modus auf "expandable_button" zurück (siehe
        // getEffectiveBannerMode()), dort greift stattdessen der normale
        // "Mehr anzeigen"-Klick-Handler oben in diesem Listener.
        if (this.getEffectiveBannerMode() === 'expandable_hover') {
            this.setupSmoothHoverAnimation();
        }
    }

    handleToggleClick(toggle) {
        const category = toggle.dataset.category;
        if (!category) return;

        const isChecked = toggle.dataset.checked === 'true';
        const newChecked = !isChecked;
        const accentColor = this.design.buttons?.accept?.background || '#0073aa';

        // Visual update
        toggle.dataset.checked = newChecked;
        toggle.setAttribute('aria-checked', newChecked);
        toggle.style.backgroundColor = newChecked ? accentColor : 'rgba(0,0,0,0.15)';

        const thumb = toggle.querySelector('.pixelmate-toggle-thumb');
        if (thumb) {
            thumb.style.left = newChecked ? '18px' : '3px';
        }

        // Sync hidden checkbox
        const checkbox = this.bannerElement.querySelector(`.pixelmate-category-checkbox[data-category="${category}"]`);
        if (checkbox) {
            checkbox.checked = newChecked;
        }
    }

    setupSmoothHoverAnimation() {
        const banner = this.bannerElement;
        const bannerInner = banner.querySelector('.pixelmate-banner-inner');
        const contentElements = this.getExpandableContentElements();

        if (!this.expander) this.expander = new SmoothExpander();

        let hoverTimer = null;

        // Hover Enter — kurzer Debounce (50ms) für natürliches Gefühl
        banner.addEventListener('mouseenter', () => {
            clearTimeout(hoverTimer);

            if (this.isExpanded) return;

            hoverTimer = setTimeout(() => {
                this.expander.expand(bannerInner, contentElements, {
                    duration: 600,
                    contentDelay: 0.4,
                    imageWidth: this.design.image?.width || '80px',
                    imageHeight: this.design.image?.height || '80px',
                    bannerElement: banner,
                    targetWidth: this.getBannerWidth(),
                    collapsedWidth: this.getCollapsedWidth(),
                    onComplete: () => {
                        this.isExpanded = true;
                        this.capInnerHeightToViewport(bannerInner);
                    }
                });

                // Box-Shadow sanft verstärken
                if (bannerInner) {
                    bannerInner.style.transition += ', box-shadow 0.4s ease-out';
                    bannerInner.style.boxShadow = this.getEnhancedBoxShadow();
                }
            }, 50);
        });

        // Tastaturäquivalent zum Hover: Sobald ein Element im Banner den Fokus
        // erhält, wird ebenfalls expandiert. Ohne das bliebe der ausklappbare
        // Inhalt (Links, Kategorien, je nach Konfiguration auch die Buttons) für
        // Tastaturnutzer unerreichbar, weil er nur per Mauszeiger erscheint.
        banner.addEventListener('focusin', () => {
            // Der programmatische Erst-Fokus auf den "Ablehnen"-Button beim Laden
            // (siehe focusBanner()) ist kein echter Besucher-Fokuswechsel und soll
            // das Banner nicht aufklappen - nur einmal überspringen, danach
            // funktioniert Tab-Fokus wie gewohnt.
            if (this._suppressNextFocusExpand) {
                this._suppressNextFocusExpand = false;
                return;
            }

            clearTimeout(hoverTimer);

            if (this.isExpanded) return;

            this.expander.expand(bannerInner, contentElements, {
                duration: 600,
                contentDelay: 0.4,
                imageWidth: this.design.image?.width || '80px',
                imageHeight: this.design.image?.height || '80px',
                bannerElement: banner,
                targetWidth: this.getBannerWidth(),
                collapsedWidth: this.getCollapsedWidth(),
                onComplete: () => {
                    this.isExpanded = true;
                    this.capInnerHeightToViewport(bannerInner);
                }
            });
        });

        // Verlässt der Fokus den Banner vollständig, wieder einklappen.
        banner.addEventListener('focusout', (e) => {
            // relatedTarget ist das Element, das den Fokus erhält. Liegt es weiterhin
            // im Banner, war es nur ein Sprung zwischen zwei Elementen darin.
            if (e.relatedTarget && banner.contains(e.relatedTarget)) {
                return;
            }
            if (!this.isExpanded || banner.matches(':hover')) {
                return;
            }

            this.expander.collapse(bannerInner, contentElements, this.getCollapsedMaxHeight(), {
                duration: 500,
                bannerElement: banner,
                collapsedWidth: this.getCollapsedWidth(),
                expandedWidth: this.getBannerWidth(),
                onComplete: () => { this.isExpanded = false; }
            });
        });

        // Hover Leave — 150ms Debounce
        banner.addEventListener('mouseleave', () => {
            clearTimeout(hoverTimer);

            if (!this.isExpanded) return;

            // Solange der Fokus im Banner liegt, bleibt er offen (Tastaturnutzung).
            if (banner.contains(document.activeElement)) return;

            hoverTimer = setTimeout(() => {
                this.expander.collapse(bannerInner, contentElements, this.getCollapsedMaxHeight(), {
                    duration: 500,
                    bannerElement: banner,
                    collapsedWidth: this.getCollapsedWidth(),
                    expandedWidth: this.getBannerWidth(),
                    onComplete: () => {
                        this.isExpanded = false;
                        // Box-Shadow zurücksetzen
                        if (bannerInner) {
                            bannerInner.style.boxShadow = this.getBoxShadowString();
                        }
                    }
                });
            }, 150);
        });
    }

    getEnhancedBoxShadow() {
        if (!this.design.shadow?.enabled) return 'none';

        const s = this.design.shadow;
        const enhancedBlur = parseInt(s.blur || '12px') * 1.5 + 'px';
        const enhancedSpread = parseInt(s.spread || '0') + 2 + 'px';

        return `${s.x || '0'} ${s.y || '4px'} ${enhancedBlur} ${enhancedSpread} ${s.color || 'rgba(0, 0, 0, 0.15)'}`;
    }

    showExtendedTextWithAnimation() {
        const extendedText = this.bannerElement.querySelector('.pixelmate-extended-text');
        const bannerInner = this.bannerElement.querySelector('.pixelmate-banner-inner');

        if (extendedText && !this.isExpanded) {
            // Setze height auf auto temporär um die finale Höhe zu messen
            bannerInner.style.height = 'auto';
            extendedText.style.display = 'block';
            extendedText.style.opacity = '0';

            const finalHeight = bannerInner.offsetHeight;

            // Zurück auf die aktuelle Höhe und dann animieren
            extendedText.style.display = 'none';
            const currentHeight = bannerInner.offsetHeight;
            bannerInner.style.height = currentHeight + 'px';
            bannerInner.style.transition = 'height 0.3s ease, opacity 0.3s ease';

            // Extended text anzeigen
            extendedText.style.display = 'block';

            requestAnimationFrame(() => {
                bannerInner.style.height = finalHeight + 'px';
                extendedText.style.opacity = '1';
            });

            this.isExpanded = true;
        }
    }

    hideExtendedTextWithAnimation() {
        const extendedText = this.bannerElement.querySelector('.pixelmate-extended-text');
        const bannerInner = this.bannerElement.querySelector('.pixelmate-banner-inner');

        if (extendedText && this.isExpanded) {
            // Aktuelle Höhe erfassen
            const currentHeight = bannerInner.offsetHeight;
            bannerInner.style.height = currentHeight + 'px';
            bannerInner.style.transition = 'height 0.3s ease';

            // Extended text ausblenden
            extendedText.style.opacity = '0';

            // Höhe auf collapsed state berechnen
            extendedText.style.display = 'none';
            const collapsedHeight = bannerInner.offsetHeight;
            extendedText.style.display = 'block';

            requestAnimationFrame(() => {
                bannerInner.style.height = collapsedHeight + 'px';
            });

            setTimeout(() => {
                extendedText.style.display = 'none';
                bannerInner.style.height = 'auto';
                bannerInner.style.transition = '';
            }, 300);

            this.isExpanded = false;
        }
    }

    cacheElementReferences() {
        this.titleElement = this.bannerElement.querySelector('.pixelmate-title');
        this.textElement = this.bannerElement.querySelector('.pixelmate-text');
        this.extendedTextElement = this.bannerElement.querySelector('.pixelmate-extended-text');
        this.categoryInfoElement = this.bannerElement.querySelector('.pixelmate-category-info');
        this.moreButtonElement = this.bannerElement.querySelector('.pixelmate-more-btn');
        this.serviceDetailsToggleElement = this.bannerElement.querySelector('.pixelmate-service-details-toggle');
        this.saveSelectionButtonElement = this.bannerElement.querySelector('.pixelmate-btn-saveSelection');

        this.buttonElements = {
            accept: this.bannerElement.querySelector('.pixelmate-btn-accept'),
            reject: this.bannerElement.querySelector('.pixelmate-btn-reject'),
            settings: this.bannerElement.querySelector('.pixelmate-btn-settings'),
            saveSelection: this.bannerElement.querySelector('.pixelmate-btn-saveSelection')
        };
        this.privacyLinkElement = this.bannerElement.querySelector('.pixelmate-privacy-link');
        this.imprintLinkElement = this.bannerElement.querySelector('.pixelmate-imprint-link');

        this.categoryCheckboxes = {};
        this.bannerElement.querySelectorAll('.pixelmate-category-checkbox').forEach(cb => {
            this.categoryCheckboxes[cb.dataset.category] = cb;
        });
    }

    switchLanguageOptimized(languageCode) {
        pmLog('Switching language to:', languageCode);

        const newTexts = this.textCache[languageCode];
        if (!newTexts) {
            pmWarn('No texts found for language:', languageCode);
            return;
        }

        this.animateTextChange(newTexts, languageCode);
    }

    animateTextChange(newTexts, languageCode) {
        const contentArea = this.bannerElement.querySelector('.pixelmate-content-area');
        if (!contentArea) return;

        contentArea.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
        contentArea.style.transform = 'translateX(-20px)';
        contentArea.style.opacity = '0.3';

        setTimeout(() => {
            this.updateTextElements(newTexts, languageCode);
            this.settings.language = languageCode;

            contentArea.style.transform = 'translateX(20px)';

            requestAnimationFrame(() => {
                contentArea.style.transform = 'translateX(0)';
                contentArea.style.opacity = '1';
            });

            setTimeout(() => {
                contentArea.style.transition = '';
            }, 300);

        }, 150);
    }

    updateTextElements(newTexts, languageCode) {
        this.texts = newTexts;

        if (this.bannerElement) {
            this.bannerElement.setAttribute('lang', PixelmateBanner.resolveDefaultTextsLang(languageCode));
        }

        if (this.titleElement && newTexts.title) {
            this.titleElement.textContent = newTexts.title;
        }

        if (this.textElement && newTexts.text) {
            this.textElement.innerHTML = newTexts.text;
        }

        if (this.extendedTextElement && newTexts.extendedText) {
            this.extendedTextElement.innerHTML = newTexts.extendedText;
        }

        if (this.categoryInfoElement) {
            this.categoryInfoElement.textContent = this.buildCategoryInfoText(newTexts, languageCode);
        }

        if (this.moreButtonElement) {
            const buttonText = this.isExpanded ?
                (newTexts.lessButton || 'Weniger anzeigen') :
                (newTexts.moreButton || 'Mehr anzeigen');
            this.moreButtonElement.textContent = buttonText;
        }

        if (this.serviceDetailsToggleElement) {
            const isOpen = this.serviceDetailsToggleElement.getAttribute('aria-expanded') === 'true';
            this.serviceDetailsToggleElement.textContent = isOpen
                ? (newTexts.serviceDetailsLessLink || 'Details ausblenden')
                : (newTexts.serviceDetailsLink || 'Mehr Informationen zu den eingesetzten Diensten');
        }

        if (this.buttonElements.accept && newTexts.acceptButton) {
            this.buttonElements.accept.textContent = newTexts.acceptButton;
        }
        if (this.buttonElements.reject && newTexts.rejectButton) {
            this.buttonElements.reject.textContent = newTexts.rejectButton;
        }
        if (this.buttonElements.saveSelection && newTexts.saveSelectionButton) {
            this.buttonElements.saveSelection.textContent = newTexts.saveSelectionButton;
        }
        if (this.buttonElements.settings && newTexts.cookieSettingsLink) {
            this.buttonElements.settings.textContent = newTexts.cookieSettingsLink;
        }
        if (this.privacyLinkElement && newTexts.privacyLink) {
            this.privacyLinkElement.textContent = newTexts.privacyLink;
        }
        if (this.imprintLinkElement && newTexts.imprintLink) {
            this.imprintLinkElement.textContent = newTexts.imprintLink;
        }

        if (this.settings.showCategoryCheckboxes) {
            const lang = PixelmateBanner.resolveDefaultTextsLang(languageCode);
            Object.keys(this.categoryCheckboxes).forEach(catKey => {
                const nameEl = this.bannerElement.querySelector(`.pixelmate-category-name[data-category="${catKey}"]`);
                if (nameEl) {
                    // Eigene Kategorie-Übersetzung aus dem Repeater hat Vorrang, danach
                    // der zweisprachige Standardname.
                    const name = newTexts.categories?.[catKey]?.name
                        || SettingsModal.MODAL_TEXTS?.categories?.[catKey]?.name?.[lang];
                    if (name) nameEl.textContent = name;
                }
            });
        }

        this.updateLanguageSwitcherUI(languageCode);
    }

    /**
     * Markiert die aktive Sprache im Sprach-Umschalter (Fett + Textfarbe), analog
     * zum Verhalten des Sprach-Umschalters im Settings Modal.
     */
    updateLanguageSwitcherUI(languageCode) {
        const langButtons = this.bannerElement.querySelectorAll('.pixelmate-lang-btn');
        if (!langButtons.length) return;

        langButtons.forEach(btn => {
            const isActive = btn.dataset.lang === languageCode;
            btn.style.fontWeight = isActive ? '700' : '400';
            btn.style.background = isActive ? 'rgba(128,128,128,0.15)' : 'none';
            btn.setAttribute('aria-checked', String(isActive));
        });
    }

    getTextsForLanguage(languageCode) {
        const additionalLanguages = this.config.settings?.additionalLanguages || [];
        const mainLanguage = this.config.settings?.language || 'de_DE';

        if (languageCode === mainLanguage) {
            return this.getDefaultTexts();
        }

        // Sprachspezifische Standardtexte als Basis: Leere Felder erscheinen so in
        // der gewählten Sprache (z. B. Englisch) statt in der Hauptsprache.
        const base = this.getLanguageDefaults(languageCode);
        const baseCats = base.categories || {};
        const catName = (key, custom) => custom || baseCats[key]?.name || key;
        const catDesc = (key) => baseCats[key]?.description || '';

        for (const lang of additionalLanguages) {
            if (lang.language_code === languageCode) {
                return {
                    title: lang.banner_title || base.title,
                    text: lang.banner_text || base.text,
                    extendedText: lang.extended_text || base.extendedText,
                    acceptButton: lang.accept_button || base.acceptButton,
                    rejectButton: lang.reject_button || base.rejectButton,
                    saveSelectionButton: lang.save_selection_button || base.saveSelectionButton,
                    cookieSettingsLink: lang.cookie_settings_link || base.cookieSettingsLink,
                    moreButton: base.moreButton,
                    lessButton: base.lessButton,
                    serviceDetailsLink: base.serviceDetailsLink,
                    serviceDetailsLessLink: base.serviceDetailsLessLink,
                    privacyLink: lang.privacy_link || base.privacyLink,
                    imprintLink: lang.imprint_link || base.imprintLink,
                    required: base.required,
                    categories: {
                        necessary: { name: catName('necessary', lang.category_necessary), description: catDesc('necessary') },
                        analytics: { name: catName('analytics', lang.category_analytics), description: catDesc('analytics') },
                        marketing: { name: catName('marketing', lang.category_marketing), description: catDesc('marketing') },
                        external:  { name: catName('external',  lang.category_external),  description: catDesc('external') }
                    }
                };
            }
        }

        return base;
    }

    /**
     * Liefert die eingebauten Standardtexte für eine Sprache anhand des
     * Sprachpräfixes (z. B. "nl_NL" -> "nl"); alle übrigen fallen auf Englisch
     * zurück. Dies sind die mitgelieferten automatischen Übersetzungen: alle
     * 24 Amtssprachen der Europäischen Union - die 9 Hauptsprachen (siehe
     * DEFAULT_TEXTS) kommen direkt aus dem Bundle, die übrigen 15 (falls für
     * diese Website überhaupt gebraucht) aus dem serverseitig gefilterten
     * pixelmateConfig.extendedLanguageDefaults (siehe Kommentar über
     * DEFAULT_TEXTS oben).
     */
    getLanguageDefaults(languageCode) {
        const lang = PixelmateBanner.resolveDefaultTextsLang(languageCode);
        return PixelmateBanner.DEFAULT_TEXTS[lang]
            || this.config.extendedLanguageDefaults?.[lang]
            || PixelmateBanner.DEFAULT_TEXTS.en;
    }

    static resolveDefaultTextsLang(languageCode) {
        const raw = (languageCode || '').toLowerCase();
        const supported = [
            'de', 'fr', 'es', 'it', 'nl', 'pt', 'pl', 'sv', 'da', 'fi', 'cs', 'hu',
            'bg', 'hr', 'et', 'el', 'ga', 'lv', 'lt', 'mt', 'ro', 'sk', 'sl'
        ];
        return supported.find(code => raw.startsWith(code)) || 'en';
    }

    /**
     * Liest die bevorzugte Sprache des Browsers aus (navigator.language /
     * navigator.languages) und liefert deren zweistelligen Sprachpräfix in
     * Kleinbuchstaben zurück (z. B. "pl-PL" -> "pl"), oder "" falls nicht
     * ermittelbar.
     */
    static detectBrowserLanguage() {
        if (typeof navigator === 'undefined') return '';

        const raw = navigator.language
            || (Array.isArray(navigator.languages) ? navigator.languages[0] : '')
            || '';

        return raw.toLowerCase().split(/[-_]/)[0] || '';
    }

    /**
     * Überschreibt this.settings.language mit der per Browser erkannten Sprache,
     * wenn pixelmate_browser_language_autodetect aktiv ist. Betrifft
     * ausdrücklich nur die mitgelieferten Zusatzsprachen (nicht CORE_LANGUAGES) -
     * für die Hauptsprachen Deutsch, Englisch, Französisch, Spanisch, Italienisch,
     * Niederländisch, Polnisch, Dänisch und Schwedisch gilt weiterhin ausschließlich
     * die reguläre Spracherkennung (WPML/Polylang bzw. WordPress-Sprache über
     * this.config.settings.language).
     * Die Detailtexte im Settings Modal (Cookie-/Dienste-Infos) bleiben davon
     * unberührt, da sie separat anhand von this.config.settings.language
     * ermittelt werden und für alles außer de/fr/es/it/nl/pl/da/sv ohnehin auf
     * Englisch zurückfallen.
     */
    applyBrowserLanguageAutodetect() {
        const browserLang = PixelmateBanner.detectBrowserLanguage();

        if (!browserLang || PixelmateBanner.CORE_LANGUAGES.includes(browserLang)) {
            return;
        }

        // Standardtexte müssen entweder im Bundle liegen (CORE_LANGUAGES -
        // an dieser Stelle bereits ausgeschlossen) oder serverseitig als
        // extendedLanguageDefaults mitgeliefert worden sein (siehe
        // DSGVO_Pixelmate_FrontendLocalizer::get_extended_language_defaults(),
        // das dieselbe Accept-Language-Erkennung serverseitig durchführt und
        // daher denselben Sprachcode liefern sollte, sofern beide Seiten mit
        // demselben Browser-Header rechnen). Ist nichts davon vorhanden, gibt
        // es hier nichts zu tun - die Hauptsprache bleibt unangetastet.
        if (!PixelmateBanner.DEFAULT_TEXTS[browserLang] && !this.config.extendedLanguageDefaults?.[browserLang]) {
            return;
        }

        // Ist die (nicht-Kern-)Hauptsprache der Seite bereits die erkannte
        // Browsersprache, gibt es nichts zu überschreiben - die eigenen,
        // gepflegten Haupttexte sollen dann nicht durch die generischen
        // Standardtexte ersetzt werden.
        const mainLanguage = (this.config.settings?.language || '').toLowerCase();
        if (mainLanguage.startsWith(browserLang)) {
            return;
        }

        this.settings.language = browserLang;
    }

    getDefaultTexts() {
        return {
            title: this.config.settings?.texts?.title || '',
            text: this.config.settings?.texts?.text || '',
            extendedText: this.config.settings?.texts?.extendedText || '',
            acceptButton: this.config.settings?.texts?.acceptButton || '',
            rejectButton: this.config.settings?.texts?.rejectButton || '',
            saveSelectionButton: this.config.settings?.texts?.saveSelectionButton || 'Auswahl speichern',
            cookieSettingsLink: this.config.settings?.texts?.cookieSettingsLink || 'Einstellungen',
            moreButton: this.config.settings?.texts?.moreButton || 'Mehr anzeigen',
            lessButton: this.config.settings?.texts?.lessButton || 'Weniger anzeigen',
            serviceDetailsLink: this.config.settings?.texts?.serviceDetailsLink || 'Mehr Informationen zu den eingesetzten Diensten',
            serviceDetailsLessLink: this.config.settings?.texts?.serviceDetailsLessLink || 'Details ausblenden',
            privacyLink: this.config.settings?.texts?.privacyLink || 'Datenschutz',
            imprintLink: this.config.settings?.texts?.imprintLink || 'Impressum',
            required: this.config.settings?.texts?.required || 'Erforderlich',
            categories: this.config.settings?.texts?.categories || {
                necessary: { name: 'Notwendig', description: '' },
                analytics: { name: 'Statistik', description: '' },
                marketing: { name: 'Marketing', description: '' },
                external: { name: 'Externe Medien', description: '' }
            }
        };
    }

    getDefaultText(key) {
        const defaultTexts = this.getDefaultTexts();
        return defaultTexts[key] || '';
    }

    handleButtonClick(action) {
        switch (action) {
            case 'accept':
                this.acceptAll();
                break;
            case 'reject':
                this.rejectAll();
                break;
            case 'saveSelection':
                this.saveSelection();
                break;
            case 'settings':
                this.openSettings();
                break;
        }
    }

    saveSelection() {
        const selectedCategories = {
            necessary: true,
            analytics: false,
            marketing: false,
            external: false
        };

        const checkboxes = this.bannerElement.querySelectorAll('.pixelmate-category-checkbox');
        checkboxes.forEach(checkbox => {
            const category = checkbox.dataset.category;
            if (category && category !== 'necessary') {
                selectedCategories[category] = checkbox.checked;
            }
        });

        const hasNonNecessary = selectedCategories.analytics || selectedCategories.marketing || selectedCategories.external;
        const consentType = hasNonNecessary ? 'accept' : 'reject';

        // WICHTIG: Wenn CSP aktiv ist, ist ein Reload NOTWENDIG!
        // Die CSP wird beim initialen Seitenaufruf als <meta>-Tag gesendet und kann
        // nicht nachträglich im Browser geändert werden. Ein Reload ist also IMMER
        // erforderlich, damit der Server die neue CSP-Whitelist sendet – unabhängig
        // von der reloadOnConsentChange-Einstellung.
        if (this.csp.active || this.csp.reloadOnConsentChange) {
            this.setConsent(consentType, { categories: selectedCategories, services: this.scriptBlocker.serviceConsent });
            this.trackingDataPreserver.saveTrackingDataBeforeReload();
            window.location.reload();
        } else {
            const result = this.scriptBlocker.updateConsent(selectedCategories);
            this.setConsent(consentType, result);
            this.contentBlocker.unblockAllConsentedServices();
            this.hideBanner();
        }
    }

    acceptAll() {
        // WICHTIG: Wenn CSP aktiv ist, ist ein Reload NOTWENDIG!
        // Die CSP wird beim initialen Seitenaufruf als <meta>-Tag gesendet und kann
        // nicht nachträglich im Browser geändert werden. Ein Reload ist also IMMER
        // erforderlich, damit der Server die neue CSP-Whitelist (mit freigegebenen
        // Services) sendet – unabhängig von der reloadOnConsentChange-Einstellung.
        if (this.csp.active || this.csp.reloadOnConsentChange) {
            const allCategories = { categories: { necessary: true, analytics: true, marketing: true, external: true }, services: {} };
            this.setConsent('accept', allCategories);
            this.trackingDataPreserver.saveTrackingDataBeforeReload();
            window.location.reload();
            return;
        }

        // Kategorien akzeptieren und Consent speichern
        const categories = this.scriptBlocker.acceptAll();
        this.setConsent('accept', categories);

        const proceed = () => {
            this.contentBlocker.unblockAllConsentedServices();
            this.hideBanner();
        };

        // Wenn Kategorie-Checkboxen sichtbar sind: Toggles animieren, dann erst schließen
        if (this.settings.showCategoryCheckboxes && this.bannerElement) {
            this.animateTogglesOn(proceed);
        } else {
            proceed();
        }
    }

    animateTogglesOn(onComplete) {
        const accentColor = this.design.buttons?.accept?.background || '#0073aa';
        const toggles = Array.from(
            this.bannerElement.querySelectorAll('.pixelmate-toggle-clickable[data-checked="false"]')
        );

        if (!toggles.length) {
            setTimeout(onComplete, 150);
            return;
        }

        // Toggles gestaffelt einschalten: ~80ms Abstand, danach 150ms Pause vor dem Schließen
        const stagger = Math.min(80, 300 / toggles.length);
        toggles.forEach((toggle, i) => {
            setTimeout(() => {
                toggle.dataset.checked = 'true';
                toggle.setAttribute('aria-checked', 'true');
                toggle.style.backgroundColor = accentColor;
                const thumb = toggle.querySelector('.pixelmate-toggle-thumb');
                if (thumb) thumb.style.left = '18px';

                const checkbox = this.bannerElement.querySelector(
                    `.pixelmate-category-checkbox[data-category="${toggle.dataset.category}"]`
                );
                if (checkbox) checkbox.checked = true;
            }, i * stagger);
        });

        const totalDelay = (toggles.length - 1) * stagger + 150;
        setTimeout(onComplete, totalDelay);
    }

    rejectAll() {
        pmLog('User rejected all cookies');

        // Alle nicht-notwendigen Kategorien ablehnen
        const categories = this.scriptBlocker.rejectAll();

        // Consent speichern
        this.setConsent('reject', categories);

        // Banner verstecken
        this.hideBanner();
    }

    openSettings() {
        pmLog('Opening settings modal');
        this.settingsModal.open();
    }

    /**
     * Erstellt einen permanent sichtbaren, kleinen "Cookies"-Button am unteren
     * Bildschirmrand, über den die Einwilligung jederzeit widerrufen/angepasst
     * werden kann (DSGVO: Widerruf so einfach wie die Einwilligung). Übernimmt
     * Hintergrund- und Schriftfarbe aus den Banner-Design-Einstellungen.
     */
    initFloatingRevokeButton() {
        if (!this.settings.floatingRevokeButton) {
            return;
        }

        const mount = () => {
            if (document.getElementById('pixelmate-revoke-button')) {
                return;
            }

            const side = this.settings.floatingRevokeButtonPosition === 'bottom_left' ? 'left' : 'right';

            const button = document.createElement('button');
            button.type = 'button';
            button.id = 'pixelmate-revoke-button';
            button.className = 'pixelmate-revoke-button';
            button.textContent = 'Cookies';
            button.setAttribute('aria-label', this.texts.cookieSettingsLink || 'Cookie-Einstellungen öffnen');

            const styles = {
                position: 'fixed !important',
                bottom: '0 !important',
                [side]: '12px !important',
                zIndex: '999997 !important',
                boxSizing: 'border-box !important',
                fontFamily: 'inherit !important',
                fontSize: '11px !important',
                lineHeight: '1.4 !important',
                color: `${this.design.colors?.text || '#333333'} !important`,
                backgroundColor: `${this.design.colors?.background || '#ffffff'} !important`,
                padding: '4px 10px !important',
                margin: '0 !important',
                border: 'none !important',
                borderRadius: '6px 6px 0 0 !important',
                boxShadow: '0 -1px 4px rgba(0, 0, 0, 0.15) !important',
                backgroundImage: 'none !important',
                textShadow: 'none !important',
                cursor: 'pointer !important',
                opacity: '0.85 !important',
                transition: 'opacity 200ms ease !important'
            };

            button.style.cssText = Object.entries(styles)
                .map(([key, value]) => `${this.camelToKebab(key)}: ${value}`)
                .join('; ');

            button.addEventListener('mouseenter', () => button.style.setProperty('opacity', '1', 'important'));
            button.addEventListener('mouseleave', () => button.style.setProperty('opacity', '0.85', 'important'));
            button.addEventListener('click', () => this.openSettings());

            document.body.appendChild(button);
            this.revokeButtonElement = button;

            // Der Widerruf-Button kann ohne Banner existieren (bereits erteilte
            // Einwilligung), deshalb hier ebenfalls die Fokus-Markierung sicherstellen.
            this.injectA11yStyles();
        };

        if (document.body) {
            mount();
        } else {
            document.addEventListener('DOMContentLoaded', mount);
        }
    }

    toggleExtendedText() {
        const moreBtn = this.bannerElement.querySelector('.pixelmate-more-btn');
        const bannerInner = this.bannerElement.querySelector('.pixelmate-banner-inner');
        const contentElements = this.getExpandableContentElements();

        if (!moreBtn) return;

        if (!this.expander) this.expander = new SmoothExpander();

        if (this.isExpanded) {
            // Kollabieren
            moreBtn.textContent = this.texts.moreButton || 'Mehr anzeigen';

            this.expander.collapse(bannerInner, contentElements, this.getCollapsedMaxHeight(), {
                duration: 500,
                bannerElement: this.bannerElement,
                collapsedWidth: this.getCollapsedWidth(),
                expandedWidth: this.getBannerWidth(),
                onComplete: () => { this.isExpanded = false; }
            });
        } else {
            // Erweitern
            moreBtn.textContent = this.texts.lessButton || 'Weniger anzeigen';

            this.expander.expand(bannerInner, contentElements, {
                duration: 600,
                contentDelay: 0.4,
                imageWidth: this.design.image?.width || '80px',
                imageHeight: this.design.image?.height || '80px',
                bannerElement: this.bannerElement,
                targetWidth: this.getBannerWidth(),
                collapsedWidth: this.getCollapsedWidth(),
                onComplete: () => {
                    this.isExpanded = true;
                    // Nach der Expansion auf die Viewport-Grenze deckeln, falls der
                    // Inhalt höher als der Bildschirm ist — der Banner bleibt so im
                    // Sichtfeld, der Scroll-Bereich übernimmt das interne Scrollen.
                    this.capInnerHeightToViewport(bannerInner);
                }
            });
        }
    }

    getExpandableContentElements() {
        const banner = this.bannerElement;
        const elements = [];

        const extendedText = banner.querySelector('.pixelmate-extended-text');
        if (extendedText) elements.push({ el: extendedText, type: 'text' });

        // "Mehr Informationen zu den eingesetzten Diensten": in erweiterbaren Modi
        // ohne eigenen Klick-Toggle gerendert (siehe getServiceDetailsHTML()) -
        // erscheint hier zusammen mit dem erweiterten Text automatisch beim
        // Erweitern/Hover des Banners.
        const serviceDetails = banner.querySelector('.pixelmate-service-details-panel');
        if (serviceDetails) elements.push({ el: serviceDetails, type: 'text' });

        const isExpandable = this.settings.bannerMode !== 'standard';
        if (isExpandable) {
            // Kategorien nur einschließen wenn sie initial versteckt sind
            if (this.settings.hideCategoriesUntilExpanded) {
                const categoryCheckboxes = banner.querySelector('.pixelmate-category-checkboxes');
                if (categoryCheckboxes) elements.push({ el: categoryCheckboxes, type: 'checkboxes' });
            }
        }

        if (this.settings.hideImageUntilExpanded) {
            const image = banner.querySelector('.pixelmate-banner-image');
            if (image) elements.push({ el: image, type: 'image' });
        }

        // Buttons und Footer stehen außerhalb des Scroll-Bereichs (siehe
        // getBannerHTML()) und sind immer sichtbar - sie werden hier bewusst
        // NICHT mehr als expandierbarer Content behandelt.

        return elements;
    }

    getCollapsedMaxHeight() {
        return this.getSizeConfig().collapsedMaxHeight || '150px';
    }

    /**
     * Wandelt einen konfigurierten CSS-Längenwert (px, vh, oder eine reine Zahl ohne
     * Einheit — ein häufiger Tippfehler in den Design-Einstellungen) in eine konkrete
     * Pixelzahl um. Gibt null zurück für alles, was sich nicht sicher auflösen lässt
     * (%, em, calc(), leer, 'auto', 'none' ...) — der Aufrufer muss dann einen Fallback
     * verwenden statt den Rohwert unverändert in eine CSS-Deklaration zu übernehmen.
     */
    parseLengthToPx(value, viewportPx) {
        if (value === undefined || value === null) return null;
        const str = String(value).trim();
        if (str === '' || str === 'auto' || str === 'none') return null;

        const vh = str.match(/^(-?\d*\.?\d+)vh$/);
        if (vh) return (parseFloat(vh[1]) / 100) * viewportPx;

        const px = str.match(/^(-?\d*\.?\d+)px$/);
        if (px) return parseFloat(px[1]);

        // Reine Zahl ohne Einheit (z.B. "20" statt "20px") — defensiv als px behandeln,
        // da border-width & Co. sonst als ungültige CSS-Deklaration verworfen würden.
        if (/^-?\d*\.?\d+$/.test(str)) return parseFloat(str);

        return null;
    }

    /**
     * Stellt sicher, dass ein konfigurierter CSS-Längenwert eine gültige Einheit hat.
     * Carbon-Fields-Zahlenfelder liefern teils reine Zahlen ohne Einheit (z.B. "1"
     * statt "1px") — das ist für border-width ungültiges CSS und wird vom Browser
     * stillschweigend verworfen (die ganze Deklaration fehlt dann im Style).
     */
    normalizeCssLength(value, fallback) {
        if (value === undefined || value === null || String(value).trim() === '') {
            return fallback;
        }
        const str = String(value).trim();
        if (/^-?\d*\.?\d+$/.test(str)) {
            return str === '0' ? '0' : `${str}px`;
        }
        return str;
    }

    /**
     * Liefert eine an der Viewport-Höhe orientierte Obergrenze für die Banner-Höhe,
     * als konkreter Pixelwert (statt CSS calc()/min()). Damit bleibt der Banner in
     * JEDEM Format (Leiste, minimal, Fullscreen) und auf jedem Gerät vollständig im
     * sichtbaren Bereich — auch wenn sehr viel Text bzw. viele Kategorien konfiguriert
     * sind. Die vertikalen Ränder (margin top/bottom) werden abgezogen, damit
     * Schatten/Rahmen nicht am Bildschirmrand kleben.
     *
     * Bewusst als in JS berechneter px-Wert statt als CSS calc()/min()-Ausdruck: auf
     * mindestens einer Installation wurden calc()/min()-Deklarationen vom Browser
     * komplett verworfen (die max-height fehlte dann ganz), obwohl sie syntaktisch
     * gültig aussahen. Ein reiner px-Wert kann nicht auf diese Weise scheitern.
     */
    getViewportMaxHeight() {
        const bannerStyle = this.design.bannerStyle || 'leiste_unten';
        const viewportPx = window.innerHeight || document.documentElement.clientHeight || 0;

        // Zentriertes Vollbild-Modal: oben und unten etwas Luft lassen.
        if (bannerStyle === 'fullscreen') {
            return `${Math.max(0, viewportPx - 40)}px`;
        }

        const m = this.getSpacingConfig().margin || {};
        const topPx = this.parseLengthToPx(m.top, viewportPx) ?? 0;
        const bottomPx = this.parseLengthToPx(m.bottom, viewportPx) ?? 20;

        return `${Math.max(0, viewportPx - topPx - bottomPx)}px`;
    }

    getExpandedMaxHeight() {
        const capString = this.getViewportMaxHeight();
        const capPx = parseFloat(capString);
        const viewportPx = window.innerHeight || document.documentElement.clientHeight || 0;
        const configured = this.getSizeConfig().maxHeight;

        // 'auto'/'none' bedeuten "keine feste Höhe konfiguriert". Ein nicht auflösbarer
        // Wert (%, em, calc(), Tippfehler ...) wird ebenso ignoriert, statt ihn
        // unverändert in eine CSS-Deklaration zu übernehmen, die dann ungültig sein
        // und komplett verworfen werden könnte.
        const configuredPx = (!configured || configured === 'none' || configured === 'auto')
            ? null
            : this.parseLengthToPx(configured, viewportPx);

        if (configuredPx === null) {
            return capString;
        }

        // Konfigurierte Maximalhöhe respektieren, aber nie über die Viewport-Grenze
        // hinaus (sonst kann der expandierte Banner aus dem Sichtfeld wachsen).
        return `${Math.min(configuredPx, capPx)}px`;
    }

    setConsent(type, consentData, meta = {}) {
        const duration = type === 'accept'
            ? this.cookies.acceptDuration || 365
            : this.cookies.rejectDuration || 30;

        // Consent-Objekt mit Kategorien und Services erstellen
        const fullConsentData = {
            type: type,
            categories: consentData.categories || consentData || {
                necessary: true,
                analytics: false,
                marketing: false,
                external: false
            },
            services: consentData.services || {},
            timestamp: Date.now(),
            // Versions-Kennung der Konfiguration, der zugestimmt wurde. Dient als
            // Grundlage für Re-Consent bei späteren Änderungen.
            version: this.consentVersion
        };

        // Herkunft eines automatisch (per GPC) erzeugten Consents markieren,
        // damit Protokollierung und spätere Auswertung dies unterscheiden können.
        if (meta.gpc) {
            fullConsentData.gpc = true;
        }
        if (meta.dnt) {
            fullConsentData.dnt = true;
        }

        this.setCookie(this.cookieName, fullConsentData, duration);

        // Keep the in-page early-consent state in sync so the early content blocker
        // (YouTubeBlocker::inject_early_blocking_script) immediately honours the new
        // consent without requiring a page reload. Without this, pixelmateEarlyConsent
        // stays null (set at page-load when no cookie existed) and the property-override
        // src setters would still intercept and block YouTube iframes restored by
        // ContentBlocker, causing the "double-click to unlock" issue.
        // Guard: only update if the inline script already initialised the variable
        // (pixelmateEarlyConsent === null on first page load, undefined means the
        // inline script did not run — e.g. in unit tests — and we must not pollute
        // the global state there).
        if (typeof window.pixelmateEarlyConsent !== 'undefined') {
            window.pixelmateEarlyConsent = fullConsentData;
        }

        if (this.cookies.loggingEnabled) {
            this.logConsent(type, fullConsentData);
        }

        // Statusansage für Screenreader-Nutzer (WCAG 4.1.3): ohne aria-live-
        // Region bekommen sie nicht mit, dass ihre Auswahl übernommen wurde -
        // anders als sehende Nutzer, für die Banner/Modal einfach verschwinden.
        // meta.announceText erlaubt Aufrufern mit einem eigenen Sprachkontext
        // (z.B. SettingsModal.saveSelection(), dessen Sprachumschalter
        // unabhängig von der Banner-Sprache ist) eine passend übersetzte
        // Ansage zu übergeben, statt sich auf this.texts (Banner-Sprache) zu
        // verlassen.
        this.announceToScreenReader(meta.announceText || this.getConsentAnnouncementText(type));
    }

    /**
     * Liefert die für den jeweiligen Consent-Typ passende, für Screenreader
     * vorzulesende Statusmeldung in der aktuellen Banner-Sprache (this.texts).
     * "custom" (Auswahl speichern) und die GPC/DNT-Auto-Ablehnung nutzen
     * dieselben Texte wie "reject"/"accept" mangels eigener Übersetzung -
     * inhaltlich passend, da beide Fälle Kategorien gezielt (de-)aktivieren.
     */
    getConsentAnnouncementText(type) {
        const key = type === 'accept'
            ? 'consentAcceptedAnnouncement'
            : type === 'custom'
                ? 'consentSavedAnnouncement'
                : 'consentRejectedAnnouncement';
        return this.texts?.[key] || PixelmateBanner.DEFAULT_TEXTS.en[key];
    }

    /**
     * Sagt eine Statusänderung für Screenreader-Nutzer an, ohne sie visuell
     * darzustellen (role="status"/aria-live="polite"). Die Region wird einmalig
     * lazy erzeugt und für alle folgenden Ansagen wiederverwendet. Der Text wird
     * zunächst geleert und erst leicht verzögert gesetzt, da manche Screenreader
     * eine unveränderte aria-live-Region (identischer Text wie zuvor) sonst
     * nicht erneut vorlesen.
     */
    announceToScreenReader(message) {
        if (!message) return;

        let region = document.getElementById('pixelmate-status-announcer');
        if (!region) {
            region = document.createElement('div');
            region.id = 'pixelmate-status-announcer';
            region.setAttribute('role', 'status');
            region.setAttribute('aria-live', 'polite');
            // Inline statt per CSS-Klasse versteckt, da assets/public/css/pixelmate.css
            // im echten Frontend nicht geladen wird (siehe injectA11yStyles()).
            region.style.cssText = 'position:absolute !important;width:1px !important;height:1px !important;padding:0 !important;margin:-1px !important;overflow:hidden !important;clip:rect(0,0,0,0) !important;white-space:nowrap !important;border:0 !important;';
            document.body.appendChild(region);
        }

        region.textContent = '';
        window.setTimeout(() => { region.textContent = message; }, 50);
    }

    logConsent(type, consentData) {
        if (!this.config.ajax) {
            return Promise.resolve(null);
        }

        return this.ajaxPost('pixelmate_log_consent', {
            consent_type: type,
            consent_data: JSON.stringify(consentData),
            banner_version: this.consentVersion || '',
            url: this.urls.current || window.location.href,
            timestamp: new Date().toISOString()
        }).catch(error => {
            pmError('Logging error:', error);
            return null;
        });
    }

    /**
     * Sendet eine POST-Anfrage an admin-ajax.php und wiederholt sie genau einmal
     * mit frischer Nonce, wenn der Server die mitgesendete abgelehnt hat.
     *
     * Hintergrund: Die Nonce wird beim Rendern ins HTML geschrieben und damit vom
     * Seiten-Cache mitkonserviert. Für nicht eingeloggte Besucher ist sie höchstens
     * 24 Stunden gültig — liefert der Cache dieselbe Seite länger aus, scheitert
     * jede darauf gestützte Anfrage. Beim Consent-Logging hieß das bisher: der
     * Einwilligungsnachweis fehlt, ohne dass es irgendwo auffällt.
     *
     * @param {string} action AJAX-Action ohne Präfix.
     * @param {Object} params Zusätzliche POST-Parameter.
     * @param {boolean} allowRetry Interner Schutz gegen Endlosschleifen.
     * @returns {Promise<Object|null>} Geparste JSON-Antwort oder null.
     */
    async ajaxPost(action, params = {}, allowRetry = true) {
        const ajax = this.config.ajax;
        if (!ajax || !ajax.url) {
            return null;
        }

        const send = (nonce, isRetry) => fetch(ajax.url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ action, nonce: nonce || '', retry: isRetry ? '1' : '0', ...params })
        });

        let response = await send(ajax.nonce, false);
        let data = await this.parseJsonSafely(response);

        if (allowRetry && this.isNonceFailure(response, data)) {
            const freshNonce = await this.refreshNonce();
            if (freshNonce) {
                response = await send(freshNonce, true);
                data = await this.parseJsonSafely(response);
            }
        }

        return data;
    }

    /**
     * Erkennt, ob eine Antwort an einer abgelehnten Nonce gescheitert ist.
     */
    isNonceFailure(response, data) {
        if (response && response.status === 403) {
            return true;
        }
        return !!(data && data.success === false && data.data && data.data.code === 'invalid_nonce');
    }

    /**
     * Holt eine frische Nonce und merkt sie für alle weiteren Anfragen dieser Seite.
     *
     * @returns {Promise<string|null>}
     */
    async refreshNonce() {
        const ajax = this.config.ajax;
        if (!ajax || !ajax.url) {
            return null;
        }

        try {
            const response = await fetch(ajax.url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({ action: 'pixelmate_refresh_nonce' })
            });
            const data = await this.parseJsonSafely(response);
            const nonce = data && data.success && data.data ? data.data.nonce : null;

            if (nonce) {
                ajax.nonce = nonce;
            }

            return nonce;
        } catch (error) {
            pmError('Nonce refresh failed:', error);
            return null;
        }
    }

    /**
     * Liest eine Antwort als JSON, ohne bei nicht-JSON-Antworten zu werfen
     * (z. B. wenn ein Sicherheits-Plugin eine HTML-Fehlerseite zurückgibt).
     */
    async parseJsonSafely(response) {
        try {
            return await response.json();
        } catch (error) {
            return null;
        }
    }

    animateIn() {
        requestAnimationFrame(() => {
            const bannerStyle = this.design.bannerStyle || 'leiste_unten';

            this.bannerElement.style.opacity = '1';

            if (bannerStyle === 'fullscreen') {
                this.bannerElement.style.transform = 'translate(-50%, -50%)';
            } else {
                this.bannerElement.style.transform = 'none';
            }
        });
    }

    hideBanner() {
        if (!this.isVisible) return;

        const exitAnimation = this.design.animations?.exit || 'slideOutDown';
        const exitDuration = this.design.animations?.exitDuration || 300;

        this.bannerElement.style.transition = `all ${exitDuration}ms ${this.design.animations?.easing || 'ease-out'}`;
        this.bannerElement.style.opacity = '0';
        this.bannerElement.style.transform = this.getExitTransform(exitAnimation);

        setTimeout(() => {
            if (this.bannerElement) {
                this.bannerElement.remove();
            }
            if (this.overlayElement) {
                this.overlayElement.remove();
            }
            this.isVisible = false;
        }, exitDuration);
    }

    getInitialTransform() {
        const animation = this.design.animations?.entrance || 'slideInUp';
        const bannerStyle = this.design.bannerStyle || 'leiste_unten';

        if (bannerStyle === 'fullscreen') {
            switch (animation) {
                case 'slideInUp':
                    return 'translate(-50%, calc(-50% + 100px))';
                case 'slideInDown':
                    return 'translate(-50%, calc(-50% - 100px))';
                case 'slideInLeft':
                    return 'translate(calc(-50% - 100px), -50%)';
                case 'slideInRight':
                    return 'translate(calc(-50% + 100px), -50%)';
                case 'zoomIn':
                    return 'translate(-50%, -50%) scale(0.5)';
                case 'bounceIn':
                    return 'translate(-50%, -50%) scale(0.3)';
                case 'flipInX':
                    return 'translate(-50%, -50%) rotateX(-90deg)';
                case 'flipInY':
                    return 'translate(-50%, -50%) rotateY(-90deg)';
                case 'fadeIn':
                case 'none':
                default:
                    return 'translate(-50%, -50%)';
            }
        }

        switch (animation) {
            case 'slideInUp': return 'translateY(100px)';
            case 'slideInDown': return 'translateY(-100px)';
            case 'slideInLeft': return 'translateX(-100px)';
            case 'slideInRight': return 'translateX(100px)';
            case 'zoomIn': return 'scale(0.5)';
            case 'bounceIn': return 'scale(0.3)';
            case 'flipInX': return 'rotateX(-90deg)';
            case 'flipInY': return 'rotateY(-90deg)';
            case 'fadeIn':
            case 'none':
            default:
                return 'none';
        }
    }

    getExitTransform(animation) {
        const bannerStyle = this.design.bannerStyle || 'leiste_unten';

        if (bannerStyle === 'fullscreen') {
            switch (animation) {
                case 'slideOutDown':
                    return 'translate(-50%, calc(-50% + 100px))';
                case 'slideOutUp':
                    return 'translate(-50%, calc(-50% - 100px))';
                case 'slideOutLeft':
                    return 'translate(calc(-50% - 100px), -50%)';
                case 'slideOutRight':
                    return 'translate(calc(-50% + 100px), -50%)';
                case 'zoomOut':
                    return 'translate(-50%, -50%) scale(0.5)';
                case 'bounceOut':
                    return 'translate(-50%, -50%) scale(0.3)';
                case 'flipOutX':
                    return 'translate(-50%, -50%) rotateX(90deg)';
                case 'flipOutY':
                    return 'translate(-50%, -50%) rotateY(90deg)';
                case 'fadeOut':
                case 'none':
                default:
                    return 'translate(-50%, -50%)';
            }
        }

        switch (animation) {
            case 'slideOutDown': return 'translateY(100px)';
            case 'slideOutUp': return 'translateY(-100px)';
            case 'slideOutLeft': return 'translateX(-100px)';
            case 'slideOutRight': return 'translateX(100px)';
            case 'zoomOut': return 'scale(0.5)';
            case 'bounceOut': return 'scale(0.3)';
            case 'flipOutX': return 'rotateX(90deg)';
            case 'flipOutY': return 'rotateY(90deg)';
            case 'fadeOut':
            case 'none':
            default:
                return 'none';
        }
    }

    // Helper Methods
    getSpacingConfig() {
        const device = this.getDeviceType();
        const spacing = this.design.spacing;
        if (!spacing) return {};
        // New nested device structure
        if (spacing.desktop) {
            const deviceSpacing = spacing[device];
            // Fall back to desktop if device-specific values are empty
            if (deviceSpacing) {
                const resolved = {
                    padding: {},
                    margin: {}
                };
                ['padding', 'margin'].forEach(prop => {
                    const desktop = spacing.desktop[prop] || {};
                    const specific = deviceSpacing[prop] || {};
                    ['top', 'right', 'bottom', 'left'].forEach(side => {
                        resolved[prop][side] = (specific[side] && specific[side].trim()) ? specific[side] : desktop[side];
                    });
                });
                return resolved;
            }
            return spacing.desktop || {};
        }
        // Legacy flat structure fallback
        return { padding: spacing.padding, margin: spacing.margin };
    }

    getPaddingString() {
        const p = this.getSpacingConfig().padding || {};
        return `${p.top || '20px'} ${p.right || '20px'} ${p.bottom || '20px'} ${p.left || '20px'}`;
    }

    getMarginString() {
        const m = this.getSpacingConfig().margin || {};
        return `${m.top || '0'} ${m.right || '20px'} ${m.bottom || '20px'} ${m.left || '20px'}`;
    }

    getTypoFontSize(element, defaultSize) {
        const device = this.getDeviceType();
        const fontSizes = this.design.typography?.[element]?.fontSizes;
        if (!fontSizes) return this.design.typography?.[element]?.fontSize || defaultSize;
        const size = fontSizes[device];
        if (size && size.trim()) return size;
        return fontSizes.desktop || defaultSize;
    }

    getTypoLineHeight(element, defaultHeight) {
        const device = this.getDeviceType();
        const lineHeights = this.design.typography?.[element]?.lineHeights;
        if (!lineHeights) return this.design.typography?.[element]?.lineHeight || defaultHeight;
        const lh = lineHeights[device];
        if (lh && lh.trim()) return lh;
        return lineHeights.desktop || defaultHeight;
    }

    getBorderString() {
        const b = this.design.border || {};
        return `${b.width || '0'} ${b.style || 'solid'} ${b.color || '#cccccc'}`;
    }

    getBoxShadowString() {
        if (!this.design.shadow?.enabled) return 'none';

        const s = this.design.shadow;
        return `${s.x || '0'} ${s.y || '4px'} ${s.blur || '12px'} ${s.spread || '0'} ${s.color || 'rgba(0, 0, 0, 0.1)'}`;
    }

    getImageBorderString() {
        const b = this.design.image?.border || {};
        return `${b.width || '0'} ${b.style || 'solid'} ${b.color || '#cccccc'}`;
    }

    getImageContentLayout(imageHTML, contentHTML, buttonsHTML, position) {
        const contentMaxWidthStyle = `max-width: ${this.getBannerContentMaxWidth()} !important; box-sizing: border-box !important;`;

        switch (position) {
            case 'bottom':
                return `
                    <div style="display: flex !important; flex-direction: column !important; align-items: center !important; gap: 15px !important; width: 100% !important; min-height: 0 !important;">
                        <div class="pixelmate-banner-content" style="display: flex !important; flex-direction: column !important; flex: 1 !important; width: 100% !important; min-height: 0 !important; min-width: 0 !important; ${contentMaxWidthStyle}">
                            ${contentHTML}
                            ${buttonsHTML}
                        </div>
                        ${imageHTML}
                    </div>
                `;
            case 'right':
                return `
                    <div class="pixelmate-banner-content" style="display: flex !important; flex-direction: column !important; flex: 1 !important; min-height: 0 !important; min-width: 0 !important; ${contentMaxWidthStyle}">
                        ${contentHTML}
                        ${buttonsHTML}
                    </div>
                    ${imageHTML}
                `;
            case 'left':
            default:
                return `
                    ${imageHTML}
                    <div class="pixelmate-banner-content" style="display: flex !important; flex-direction: column !important; flex: 1 !important; min-height: 0 !important; min-width: 0 !important; ${contentMaxWidthStyle}">
                        ${contentHTML}
                        ${buttonsHTML}
                    </div>
                `;
        }
    }

    getButtonPaddingString() {
        const p = this.design.buttons?.styling?.padding || {};
        return `${p.top || '8px'} ${p.right || '16px'} ${p.bottom || '8px'} ${p.left || '16px'}`;
    }

    getButtonMarginString() {
        const m = this.design.buttons?.styling?.margin || {};
        return `${m.top || '0'} ${m.right || '8px'} ${m.bottom || '0'} ${m.left || '0'}`;
    }

    getMaxWidth() {
        const bannerStyle = this.design.bannerStyle || 'leiste_unten';
        const hardCap = 800;

        if (bannerStyle === 'leiste_unten') {
            return `${hardCap}px`;
        }

        // "Leiste unten" (echte volle Leiste, siehe getBarLayoutHTML()) hat bewusst
        // KEINEN 800px-Deckel - sie soll standardmäßig die volle Breite einnehmen,
        // respektiert aber eine explizit konfigurierte Max-Width.
        if (bannerStyle === 'leiste_unten_bar') {
            const configuredMaxWidth = this.getSizeConfig().maxWidth;
            return (configuredMaxWidth && configuredMaxWidth.trim()) ? configuredMaxWidth : '100vw';
        }

        const configuredMaxWidth = this.getSizeConfig().maxWidth;
        if (!configuredMaxWidth || configuredMaxWidth === '100vw') {
            return `${hardCap}px`;
        }

        // Konfigurierte Werte respektieren, aber nie über den Hard-Cap hinaus
        // (verhindert, dass die Kategorien/Buttons bei großen Viewports
        // über den Banner-Rahmen hinauswachsen).
        const parsedPx = parseFloat(configuredMaxWidth);
        if (configuredMaxWidth.endsWith('px') && !Number.isNaN(parsedPx) && parsedPx > hardCap) {
            return `${hardCap}px`;
        }

        return configuredMaxWidth;
    }

    // Content darf nie breiter werden als die tatsächlich gerenderte Banner-Box
    // (min aus konfigurierter Breite und getMaxWidth()-Deckel) abzüglich des
    // linken/rechten Innenabstands. width:100% allein reicht nicht, weil
    // Breiten-Vorgaben in vw/% relativ zum Viewport bzw. Flex-Container
    // aufgelöst werden und nicht garantiert innerhalb der Banner-Box bleiben.
    getBannerContentMaxWidth() {
        const isExpandable = this.settings.bannerMode !== 'standard';
        const configuredWidth = isExpandable ? this.getCollapsedWidth() : this.getBannerWidth();
        const cappedWidth = this.getMaxWidth();
        const padding = this.getSpacingConfig().padding || {};
        const left = padding.left || '20px';
        const right = padding.right || '20px';

        return `calc(min(${configuredWidth}, ${cappedWidth}) - ${left} - ${right})`;
    }

    getMaxHeight() {
        const bannerStyle = this.design.bannerStyle || 'leiste_unten';
        const capString = this.getViewportMaxHeight();

        // Leiste unten: keine konfigurierbare Höhe — hart auf die Viewport-Grenze
        // deckeln, damit die Leiste bei viel Text nicht über den Bildschirm wächst.
        if (bannerStyle === 'leiste_unten') {
            return capString;
        }

        const capPx = parseFloat(capString);
        const viewportPx = window.innerHeight || document.documentElement.clientHeight || 0;
        const configured = this.getSizeConfig().maxHeight;

        // 'auto'/'none' bedeuten "keine feste Höhe konfiguriert". Ein nicht auflösbarer
        // Wert wird ebenso ignoriert statt unverändert in eine (womöglich ungültige)
        // CSS-Deklaration übernommen zu werden.
        const configuredPx = (!configured || configured === 'none' || configured === 'auto')
            ? null
            : this.parseLengthToPx(configured, viewportPx);

        if (configuredPx === null) {
            return capString;
        }

        // Konfigurierte Maximalhöhe respektieren, aber nie über die Viewport-Grenze.
        return `${Math.min(configuredPx, capPx)}px`;
    }

    camelToKebab(str) {
        return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
    }

    injectCustomCSS() {
        const customCSS = this.design.customCSS;
        if (!customCSS || customCSS.trim() === '') {
            return;
        }

        let styleTag = document.getElementById('pixelmate-custom-css');

        if (!styleTag) {
            styleTag = document.createElement('style');
            styleTag.id = 'pixelmate-custom-css';
            styleTag.type = 'text/css';
            document.head.appendChild(styleTag);
        }

        const prefixedCSS = this.prefixCustomCSS(customCSS);
        styleTag.textContent = prefixedCSS;
    }

    prefixCustomCSS(css) {
        return css.replace(
            /(^|\n)(\s*)([\w\-#\.\[\],:>\+~\*\s]+)(\s*{)/gm,
            (match, lineStart, indent, selector, brace) => {
                if (selector.trim().startsWith('#pixelmate')) {
                    return match;
                }
                return lineStart + indent + '#pixelmate-banner ' + selector.trim() + brace;
            }
        );
    }

    setCookie(name, value, days) {
        const expires = new Date();
        expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));

        const cookieOptions = [
            `${name}=${encodeURIComponent(JSON.stringify(value))}`,
            `expires=${expires.toUTCString()}`,
            `path=${this.cookies.path || '/'}`,
            `SameSite=${this.cookies.sameSite || 'Lax'}`
        ];

        if (this.cookies.secure) {
            cookieOptions.push('Secure');
        }

        document.cookie = cookieOptions.join('; ');
    }

    getCookie(name) {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');

        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) {
                try {
                    return JSON.parse(decodeURIComponent(c.substring(nameEQ.length, c.length)));
                } catch (e) {
                    return c.substring(nameEQ.length, c.length);
                }
            }
        }
        return null;
    }
}